import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ProductCartCounter from '../components/ProductCartCounter';
import CombinedCheckoutModal from '../components/CombinedCheckoutModal';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { saveAqiUpdate } from '../utils/dataSaver';
import { useSkinSession } from '../context/SkinSessionContext';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId, finalizeCustomerSession } from '../utils/sessionManager';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { SEGMENTATION_API_BASE, normalizeSegmentationOption } from '../utils/segmentation';
import { getConditionDisplayName } from '../config/skinConditionDisplayNames';
import { getConditionDescription } from '../config/skinConditionDescriptions';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { persistProductCart, persistTreatmentCart } from '../utils/cartPersistence';
import { getPreviousSkinAnalysis } from '../utils/previousSkinAnalysis';
import { normalizeConditionName, normalizeDescriptionMap } from '../utils/skinConditionNormalization';
import { getDetectionsToDraw, buildSeverityBarGradientStyleVars, canonicalSkinConditionName, isSkinConditionName } from '../utils/skinResultsUtils';
import { getSeverityBarLabels } from '../utils/severityBarLabels';
import { getSideCaptures } from '../utils/sideCaptureStore';
import { fetchSkinConcernMask } from '../utils/lazySkinMask';
import { fetchStoredConditionDescriptions } from '../utils/skinAnalysisApi';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './SkinRoutinePage.css';

const DEFAULT_PRODUCT_SUGGEST_LIMIT = 4;

const parseFeatureToggle = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1' || v === 'on';
  }
  return false;
};

const toYesNoValue = (value) => {
  if (value == null) return null;
  return parseFeatureToggle(value) ? 'yes' : 'no';
};

const isSkinQualityName = (name) => isSkinConditionName(name);

const hasRecommendedProductsPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  const morning = Array.isArray(payload.morning) ? payload.morning : [];
  const evening = Array.isArray(payload.evening) ? payload.evening : [];
  return morning.length > 0 || evening.length > 0;
};

const hasRecommendedTreatmentsPayload = (payload) => {
  return Array.isArray(payload) && payload.length > 0;
};

const treatmentRowCartKey = (item, idx) => {
  if (item?.id != null) return `treatment_${item.id}`;
  if (item?.treatment_id != null) return `treatment_${item.treatment_id}`;
  const stableName = String(item?.treatment || item?.name || '').trim();
  if (stableName) return `treatment_${stableName.replace(/\s+/g, '_').toLowerCase()}`;
  return `treatment_${idx}_row`;
};

// Helper function to convert BGR to RGB
const bgrToRgb = (b, g, r) => `rgb(${r}, ${g}, ${b})`;

// BGR color definitions - match SkinResultsPage; high visibility across all skin tones
const BGR_COLORS_BY_ID = {
  0: [255, 191, 0],     // Wrinkles - cyan
  1: [50, 205, 50],     // Dry skin - lime green
  2: [255, 255, 0],     // Oily skin - neon cyan (high contrast)
  3: [255, 0, 255],     // Dark circle - magenta
  4: [0, 0, 255],       // Acne - red
  5: [60, 20, 220],     // Red spot - crimson
  6: [0, 69, 255],      // Pigmentation - red-orange
  7: [128, 128, 128],   // Unknown - mid gray
  8: [100, 100, 100],   // Mole - dark gray
  9: [170, 0, 255],     // Dark spots - vibrant fuchsia
  10: [211, 0, 148],    // Whiteheads - dark violet
  11: [255, 0, 255],    // Crow's feet - magenta
  12: [200, 200, 0],    // Open Pores - teal
  13: [225, 105, 65],   // Blackheads - royal blue
  14: [240, 32, 160],   // Clogged Pores - purple
  15: [214, 112, 218]   // Eye Bags - orchid
};

// Helper function to get color by class_id (matching SkinResultsPage - high visibility on all skin tones)
const getColorByClassId = (classId) => {
  if (BGR_COLORS_BY_ID[classId] !== undefined) {
    const [b, g, r] = BGR_COLORS_BY_ID[classId];
    return bgrToRgb(b, g, r);
  }
  return 'rgb(160, 32, 240)'; // Default - purple
};

// Class colors map - must match SkinResultsPage exactly (visibility across all skin tones)
const CLASS_COLORS_MAP = {
  'Wrinkles': 'rgb(0, 191, 255)',
  'Dry skin': 'rgb(0, 190, 255)',
  'Oily skin': 'rgb(0, 255, 255)',
  'Dark circle': 'rgb(255, 0, 255)',
  'Acne': 'rgb(255, 0, 0)',
  'Red spot': 'rgb(220, 20, 60)',
  'Pigmentation': 'rgb(255, 69, 0)',
  'Unknown': 'rgb(128, 128, 128)',
  'Mole': 'rgb(100, 100, 100)',
  'Dark spots': 'rgb(255, 0, 170)',
  'Whiteheads': 'rgb(148, 0, 211)',
  "Crow's feet": 'rgb(255, 0, 255)',
  'Open Pores': 'rgb(0, 200, 200)',
  'Blackheads': 'rgb(65, 105, 225)',
  'Clogged Pores': 'rgb(160, 32, 240)',
  'Eye Bags': 'rgb(218, 112, 214)',
  'Hydration': 'rgb(0, 200, 120)',
  'Oxygen': 'rgb(100, 149, 237)',
  'Smoothness': 'rgb(218, 112, 214)',
  'Dullness': 'rgb(205, 133, 0)',
  'Uneven Skintone': 'rgb(255, 99, 71)'
};

// Helper function to get outline color based on score range
const getScoreOutlineColor = (score) => {
  if (score >= 0 && score <= 39) {
    return 'critical'; // Red
  } else if (score >= 40 && score <= 59) {
    return 'poor'; // Orange
  } else if (score >= 60 && score <= 74) {
    return 'fair'; // Yellow/Amber
  } else if (score >= 75 && score <= 89) {
    return 'good'; // Light Green
  } else {
    return 'excellent'; // Green
  }
};

const SkinRoutinePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    const stored = sessionId ? (getSkinFlowState(sessionId) || {}) : {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const definedPatch = Object.fromEntries(Object.entries(fromLocation).filter(([, v]) => v !== undefined));
    if ('sideCaptures' in definedPatch) {
      const sc = definedPatch.sideCaptures;
      const hasAny = !!(sc && typeof sc === 'object' && (sc.left || sc.right));
      if (!hasAny) delete definedPatch.sideCaptures;
    }
    const mergedSkinResults = {
      ...(stored.skinResults && typeof stored.skinResults === 'object' ? stored.skinResults : {}),
      ...(definedPatch.skinResults && typeof definedPatch.skinResults === 'object' ? definedPatch.skinResults : {}),
    };
    const out = { ...stored, ...definedPatch, sessionId: sessionId || definedPatch.sessionId };
    if (Object.keys(mergedSkinResults).length > 0) {
      out.skinResults = mergedSkinResults;
    }
    return out;
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || null;
  const visibleConditionNames = effectiveState?.visibleConditionNames ?? null;
  const routineDataFromState = effectiveState?.routineData || null;
  const forceRegenerateForNewScan = Boolean(effectiveState?.forceRegenerateForNewScan);
  const ROUTINE_CACHE_VERSION = 2;

  const hasUsableRoutineData = (data) => {
    if (!data || typeof data !== 'object') return false;
    const hasSummary = typeof data.summary === 'string' && data.summary.trim().length > 0;
    const morning = Array.isArray(data.routine?.morning) ? data.routine.morning : [];
    const evening = Array.isArray(data.routine?.evening) ? data.routine.evening : [];
    return hasSummary || morning.length > 0 || evening.length > 0;
  };

  const getStoredRoutineData = () => {
    if (forceRegenerateForNewScan) return null;
    if (hasUsableRoutineData(routineDataFromState)) return routineDataFromState;
    if (sessionId) {
      try {
        const stored = localStorage.getItem(`saloon_routine_${sessionId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Cache schema/version guard (prevents reusing older truncated/legacy formats).
          if (parsed?.cacheVersion !== ROUTINE_CACHE_VERSION) return null;
          if (hasUsableRoutineData(parsed)) return parsed;
        }
      } catch (e) {
      }
    }
    return null;
  };

  const storedData = getStoredRoutineData();
  const getStoredRecommendationStatus = () => {
    if (forceRegenerateForNewScan) {
      return { treatmentReady: false, productReady: false };
    }
    const fromFlow = effectiveState?.recommendationStatus;
    if (fromFlow && typeof fromFlow === 'object') {
      return {
        treatmentReady: Boolean(fromFlow.treatmentReady),
        productReady: Boolean(fromFlow.productReady),
      };
    }
    if (!sessionId) {
      return { treatmentReady: false, productReady: false };
    }
    try {
      const stored = localStorage.getItem(`salon_recommendation_status_${sessionId}`);
      if (!stored) return { treatmentReady: false, productReady: false };
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object') return { treatmentReady: false, productReady: false };
      return {
        treatmentReady: Boolean(parsed.treatmentReady),
        productReady: Boolean(parsed.productReady),
      };
    } catch (e) {
      return { treatmentReady: false, productReady: false };
    }
  };

  const storedRecommendationStatus = getStoredRecommendationStatus();
  const cameBackWithRecommendations = !!(location.state && typeof location.state === 'object'
    && ('treatmentCartItems' in location.state || 'cartItems' in location.state));
  const [summary, setSummary] = useState(storedData?.summary || '');
  const [routine] = useState(storedData?.routine || { morning: [], evening: [] });
  const [recommendedTreatments, setRecommendedTreatments] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState({ morning: [], evening: [] });
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [treatmentCartItems, setTreatmentCartItems] = useState(
    () => (effectiveState?.treatmentCartItems && typeof effectiveState.treatmentCartItems === 'object' ? effectiveState.treatmentCartItems : {})
  );
  const [productCartItems, setProductCartItems] = useState(
    () => (effectiveState?.cartItems && typeof effectiveState.cartItems === 'object' ? effectiveState.cartItems : {})
  );
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [showHomeConfirmPopup, setShowHomeConfirmPopup] = useState(false);
  const [aboutPopup, setAboutPopup] = useState({ open: false, title: '', text: '' });
  const [isLoading, setIsLoading] = useState(!storedData);
  const [error, setError] = useState(null);
  const [recommendationRetryTick, setRecommendationRetryTick] = useState(0);
  // When "no", hide AI Summary section and do not show/generate it
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState(null);
  const [segmentationAiMode, setSegmentationAiMode] = useState(null);
  const [, setAiGeneratedRoutine] = useState(null);
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [serviceSuggestionEnabled, setServiceSuggestionEnabled] = useState(false);
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(false);
  const [showAdvancedCharts, setShowAdvancedCharts] = useState(true);
  const [showSkinConcerns, setShowSkinConcerns] = useState(true);
  const [showSkinConditions, setShowSkinConditions] = useState(true);
  const [showRecommendedProducts, setShowRecommendedProducts] = useState(false);
  const [suggestConfigLoaded, setSuggestConfigLoaded] = useState(false);
  const [selectedConcernName, setSelectedConcernName] = useState(() => effectiveState?.selectedCondition || null);
  const [animatedConcernName, setAnimatedConcernName] = useState(null);
  const [restoredSideCaptures, setRestoredSideCaptures] = useState(() => effectiveState?.sideCaptures || null);
  const [selectedConcernView, setSelectedConcernView] = useState(() => {
    const view = effectiveState?.imageView;
    return view === 'left' || view === 'right' || view === 'front' ? view : 'front';
  });
  const [isConcernTextExpanded, setIsConcernTextExpanded] = useState(false);
  const [selectedConcernPreviewImage, setSelectedConcernPreviewImage] = useState(
    () => skinResultsFromState?.markedImage || capturedImageFromState || null
  );
  const [aiConditionDescriptions, setAiConditionDescriptions] = useState(() => {
    const seeded = effectiveState?.aiConditionDescriptions;
    return seeded && typeof seeded === 'object' ? seeded : {};
  });
  const [isLoadingConditionDescriptions, setIsLoadingConditionDescriptions] = useState(false);
  const [previousSkinData, setPreviousSkinData] = useState(() => effectiveState?.previousSkinData || null);
  const [radarComparisonTab, setRadarComparisonTab] = useState('after');
  const [radarConcernModal, setRadarConcernModal] = useState({ open: false, index: 0 });
  const [isRadarEnlargedOpen, setIsRadarEnlargedOpen] = useState(false);
  const [activeNavSection, setActiveNavSection] = useState(null);
  const [recommendationGenerationState, setRecommendationGenerationState] = useState({
    inProgress: false,
    treatmentReady: cameBackWithRecommendations || storedRecommendationStatus.treatmentReady,
    productReady: cameBackWithRecommendations || storedRecommendationStatus.productReady,
    error: null,
  });
  const scrollShellRef = useRef(null);
  const scrollMainRef = useRef(null);
  const skinGaugeCanvasRef = useRef(null);
  const concernGaugeCopyCanvasRef = useRef(null);
  const concernGaugeOverlayCanvasRef = useRef(null);
  const concernPreviewCanvasRef = useRef(null);
  const radarModalTouchStartXRef = useRef(null);
  const recommendGenerationStartedRef = useRef(false);
  const concernSelectAnimTimeoutRef = useRef(null);
  const aqiDataFromState = effectiveState?.aqiData ?? null;
  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';
  const sideCaptures = effectiveState?.sideCaptures || restoredSideCaptures || null;
  const sideResultsRaw = skinResultsFromState?.sideResults;
  const sideResults = useMemo(() => (Array.isArray(sideResultsRaw) ? sideResultsRaw : []), [sideResultsRaw]);
  const effectivePreviousSkinData = showAdvancedCharts ? previousSkinData : null;
  const useOllamaForAi = aiGeneratedSummary !== 'no';

  useEffect(() => {
    if (showAdvancedCharts) return;
    if (previousSkinData !== null) setPreviousSkinData(null);
  }, [showAdvancedCharts, previousSkinData]);

  useEffect(() => {
    if (!showAdvancedCharts || !sessionId || previousSkinData != null) return;
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 4;

    const fetchPrevious = async () => {
      attempt += 1;
      try {
        const res = await getPreviousSkinAnalysis(sessionId, token);
        if (cancelled) return;
        if (res.success) {
          setPreviousSkinData(res.previous || null);
          return;
        }
      } catch (e) {
      }
      if (!cancelled && attempt < maxAttempts) {
        const delayMs = attempt <= 2 ? 1200 : 2200;
        setTimeout(fetchPrevious, delayMs);
      }
    };

    fetchPrevious();
    return () => { cancelled = true; };
  }, [sessionId, token, previousSkinData, showAdvancedCharts]);

  // If we navigated back from treatment/product pages, keep buttons actionable immediately.
  useEffect(() => {
    if (!cameBackWithRecommendations) return;
    setRecommendationGenerationState((prev) => ({
      ...prev,
      treatmentReady: true,
      productReady: true,
    }));
  }, [cameBackWithRecommendations]);

  // Persist recommendation readiness to flow state/localStorage so back/forward doesn't disable actions.
  useEffect(() => {
    if (!sessionId) return;
    const statusPayload = {
      treatmentReady: Boolean(recommendationGenerationState.treatmentReady),
      productReady: Boolean(recommendationGenerationState.productReady),
    };
    try {
      localStorage.setItem(`salon_recommendation_status_${sessionId}`, JSON.stringify(statusPayload));
    } catch (e) {
      // ignore
    }
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      recommendationStatus: statusPayload,
    });
  }, [sessionId, recommendationGenerationState.treatmentReady, recommendationGenerationState.productReady]);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      treatmentCartItems,
      cartItems: productCartItems,
    });
  }, [sessionId, treatmentCartItems, productCartItems]);

  const triggerConcernSelectionAnimation = useCallback((concernName) => {
    if (!concernName) return;
    if (concernSelectAnimTimeoutRef.current) {
      clearTimeout(concernSelectAnimTimeoutRef.current);
      concernSelectAnimTimeoutRef.current = null;
    }
    setAnimatedConcernName(null);
    requestAnimationFrame(() => {
      setAnimatedConcernName(concernName);
      concernSelectAnimTimeoutRef.current = setTimeout(() => {
        setAnimatedConcernName((current) => (current === concernName ? null : current));
        concernSelectAnimTimeoutRef.current = null;
      }, 900);
    });
  }, []);

  const handleSelectConcern = useCallback((concernName) => {
    const canonical = canonicalSkinConditionName(concernName) || concernName;
    setSelectedConcernName(canonical);
    triggerConcernSelectionAnimation(canonical);
  }, [triggerConcernSelectionAnimation]);

  const handleToggleConcernTextExpand = useCallback(() => {
    setIsConcernTextExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    return () => {
      if (concernSelectAnimTimeoutRef.current) {
        clearTimeout(concernSelectAnimTimeoutRef.current);
        concernSelectAnimTimeoutRef.current = null;
      }
    };
  }, []);

  const NAV_SECTIONS = useMemo(() => {
    const isSummaryEnabled = aiGeneratedSummary !== 'no';
    const items = [
      { id: 'section-user-info', label: 'Overview' },
      { id: 'section-concerns', label: 'Concerns' },
    ];
    if (showAdvancedCharts) items.push({ id: 'section-radar', label: 'Skin Analysis Report' });
    if (isSummaryEnabled) items.push({ id: 'section-ai-summary', label: 'AI Summary' });
    if (serviceSuggestionEnabled) items.push({ id: 'section-treatments', label: 'Treatments' });
    if (productSuggestionEnabled) items.push({ id: 'section-products', label: 'Products' });
    return items;
  }, [showAdvancedCharts, aiGeneratedSummary, serviceSuggestionEnabled, productSuggestionEnabled]);

  const getActiveScroller = useCallback(() => {
    const main = scrollMainRef.current;
    const shell = scrollShellRef.current;
    // Use computed style: find the element that actually overflows (can scroll)
    const canScroll = (el) =>
      el && el.scrollHeight > el.clientHeight &&
      ['auto', 'scroll'].includes(window.getComputedStyle(el).overflowY);
    if (canScroll(main)) return main;
    if (canScroll(shell)) return shell;
    return null; // window scrolls
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const scroller = getActiveScroller();
    if (scroller) {
      const elTop = el.getBoundingClientRect().top;
      const scrollerTop = scroller.getBoundingClientRect().top;
      const offset = elTop - scrollerTop + scroller.scrollTop - 80;
      scroller.scrollTo({ top: offset, behavior: 'smooth' });
    } else {
      // Fallback: window scroll
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
    }
    setActiveNavSection(sectionId);
  }, [getActiveScroller]);

  useEffect(() => {
    const scroller = getActiveScroller();
    const sectionIds = NAV_SECTIONS.map((s) => s.id);
    const observers = [];
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveNavSection(id); },
        { root: scroller, threshold: 0.2 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back to top: show when either shell or main is scrolled (shell scrolls on mobile, main on desktop)
  useEffect(() => {
    const shell = scrollShellRef.current;
    const main = scrollMainRef.current;
    const onScroll = () => {
      const shellTop = shell?.scrollTop ?? 0;
      const mainTop = main?.scrollTop ?? 0;
      setShowBackToTop(shellTop > 300 || mainTop > 300);
    };
    onScroll();
    shell?.addEventListener('scroll', onScroll, { passive: true });
    main?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      shell?.removeEventListener('scroll', onScroll);
      main?.removeEventListener('scroll', onScroll);
    };
  }, [isLoading, error]);
  const handleScrollToTop = useCallback(() => {
    scrollShellRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    scrollMainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getStoredAqiData = () => {
    if (aqiDataFromState) return aqiDataFromState;
    if (sessionId) {
      try {
        const stored = localStorage.getItem(`saloon_aqi_${sessionId}`);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
      }
    }
    return null;
  };

  const [aqiData, setAqiData] = useState(getStoredAqiData());
  const [, setAqiLoading] = useState(!getStoredAqiData());
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showFallbackSummary, setShowFallbackSummary] = useState(!storedData?.summary);
  const [isTreatmentsLoading, setIsTreatmentsLoading] = useState(false);
  const [hasCompletedTreatmentsStep, setHasCompletedTreatmentsStep] = useState(false);
  const [hasCompletedProductsStep, setHasCompletedProductsStep] = useState(false);
  const [hasCompletedDeepDiveStep, setHasCompletedDeepDiveStep] = useState(false);
  const [hasCompletedSummaryStep, setHasCompletedSummaryStep] = useState(Boolean(storedData?.summary));
  const [isRoutineBackgroundRunning, setIsRoutineBackgroundRunning] = useState(false);
  const hasGeneratedRef = useRef(!forceRegenerateForNewScan && hasUsableRoutineData(storedData));
  const hasFetchedAqiRef = useRef(!!getStoredAqiData());

  const getScoreTone = useCallback((rawScore) => {
    const score = Math.max(0, Math.min(100, Number(rawScore || 0)));
    if (score <= 39) return 'needs focused recovery';
    if (score <= 59) return 'shows visible imbalance';
    if (score <= 74) return 'is in a moderate state';
    if (score <= 89) return 'is in a good range';
    return 'is looking strong and balanced';
  }, []);

  const replaceScoreMentionsWithFriendlyWords = useCallback((text) => {
    if (!text || typeof text !== 'string') return text || '';

    const toTone = (numText) => getScoreTone(Number(numText));

    let out = text
      .replace(/\b(?:score\s*(?:is|:)?\s*)?(\d{1,3})\s*\/\s*100\b/gi, (_, n) => toTone(n))
      .replace(/\b(\d{1,3})\s*%\b/g, (_, n) => toTone(n));

    out = out.replace(/\s{2,}/g, ' ').trim();
    return out;
  }, [getScoreTone]);

  useEffect(() => {
    if (!sessionId || forceRegenerateForNewScan) return;

    const routinePayload = { summary, routine };
    if (!hasUsableRoutineData(routinePayload)) return;

    const cachePayload = {
      ...routinePayload,
      cacheVersion: ROUTINE_CACHE_VERSION,
    };

    try {
      localStorage.setItem(`saloon_routine_${sessionId}`, JSON.stringify(cachePayload));
    } catch (e) {
      // ignore localStorage failures
    }

    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      routineData: routinePayload,
    });
  }, [sessionId, forceRegenerateForNewScan, summary, routine]);

  const formatAiSummaryHtml = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    const hasPreviousScanData = Boolean(
      previousSkinData
      && typeof previousSkinData === 'object'
      && Object.keys(previousSkinData).length > 0
    );
    let s = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();

    // Remove orphan marker lines if model echoes internal markers.
    s = s.replace(/^\s*@@@HEADING@@@\s*$/gim, '');
    // Merge split heading variants into a single heading marker.
    s = s.replace(/(?:^|\n)\s*Summary\s*\n\s*With Compare Previous\s*(?=\n|$)/gi, '\n\n@@@HEADING@@@Summary With Compare Previous\n');

    const knownSectionLabels = [
      'General Cause Of Top Severity',
      'ROI Based Why This Region Is Affected',
      'Summary With Compare Previous',
      'Summary',
      'Key Concerns',
      'Why this is happening',
    ];

    // Handle flattened model output by forcing known labels onto new section boundaries.
    knownSectionLabels.forEach((label) => {
      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\s*${escaped}\\s*:?'?\\s*`, 'gi');
      s = s.replace(re, `\n\n@@@HEADING@@@${label}\n`);
    });

    // Also handle markdown/numbered section headings.
    s = s
      .replace(/\*\*\s*\d+\.\s*([^*]+?)\s*\*\*/g, '\n\n@@@HEADING@@@$1\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const formatInline = (value) => value
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/@@@HEADING@@@/g, '')
      .replace(/\s-\s/g, '\n- ');

    const headingLabelMap = {
      generalcauseoftopseverity: 'Main Causes of Your Top Skin Concerns',
      roibasedwhythisregionisaffected: 'Why These Areas Are Affected',
      summarywithcompareprevious: hasPreviousScanData ? 'Progress Since Your Previous Scan' : 'Current Scan Summary',
      summary: 'Overall Summary',
      keyconcerns: 'Key Skin Concerns',
      whythisishappening: 'Why This Is Happening',
      withcompareprevious: hasPreviousScanData ? 'Progress Since Your Previous Scan' : 'Current Scan Summary',
    };

    const normalizeHeadingKey = (value) => String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

    const getFriendlyHeading = (heading) => {
      const normalized = normalizeHeadingKey(heading);
      return headingLabelMap[normalized] || heading;
    };

    const renderBody = (body) => {
      const normalized = formatInline(body || '').replace(/\n{3,}/g, '\n\n').trim();
      if (!normalized) return '';

      const chunks = normalized.split(/\n\n+/).map((chunk) => chunk.trim()).filter(Boolean);
      return chunks.map((chunk) => {
        const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
        const isBulletList = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
        if (isBulletList) {
          const items = lines
            .map((line) => line.replace(/^[-*]\s+/, '').trim())
            .filter(Boolean)
            .map((line) => `<li>${line}</li>`)
            .join('');
          return `<ul>${items}</ul>`;
        }
        return `<p>${lines.join('<br />')}</p>`;
      }).join('');
    };

    const headingRegex = /@@@HEADING@@@([^\n]+)\n/g;
    const sections = [];
    let lastIndex = 0;
    let currentHeading = null;
    let match;

    while ((match = headingRegex.exec(s)) !== null) {
      const contentBeforeHeading = s.slice(lastIndex, match.index).trim();
      if (currentHeading || contentBeforeHeading) {
        sections.push({ heading: currentHeading, body: contentBeforeHeading });
      }
      currentHeading = (match[1] || '').trim();
      lastIndex = headingRegex.lastIndex;
    }

    const trailingContent = s.slice(lastIndex).trim();
    if (currentHeading || trailingContent) {
      sections.push({ heading: currentHeading, body: trailingContent });
    }

    const html = sections.length > 0
      ? sections
        .map(({ heading, body }) => {
          let headingText = (heading || '').trim();
          let bodyText = body;

          // Handle model output where heading is split into two lines.
          if (normalizeHeadingKey(headingText) === 'summary' && /^with compare previous\b/i.test(bodyText || '')) {
            headingText = 'Summary With Compare Previous';
            bodyText = String(bodyText || '').replace(/^with compare previous[:\s-]*/i, '').trim();
          }

          const friendlyHeading = getFriendlyHeading(headingText);
          const headingHtml = friendlyHeading ? `<h3 class="skin-routine-summary-subheading">${formatInline(friendlyHeading)}</h3>` : '';
          const bodyHtml = renderBody(bodyText);
          return `<section class="skin-routine-summary-section">${headingHtml}${bodyHtml}</section>`;
        })
        .join('')
      : renderBody(s);

    return sanitizeHtml(html);
  }, [previousSkinData]);

  // Single fetch for routine page controls (summary, products/treatments, concerns/conditions, advanced charts).
  useEffect(() => {
    if (!token) {
      setSuggestConfigLoaded(true);
      return;
    }
    let cancelled = false;
    fetchWithTimeout(`${SEGMENTATION_API_BASE}/${encodeURIComponent(token)}/segmentation`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Segmentation config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const fields = Array.isArray(data?.fields) ? data.fields : [];

        const aiSummaryValue = toYesNoValue(data?.ai_generated_summary);
        setAiGeneratedSummary(aiSummaryValue);

        const mode = String(data?.ai_mode || '').trim().toLowerCase();
        setSegmentationAiMode(mode === 'advanced' || mode === 'basic' ? mode : null);

        const aiRoutineValue = toYesNoValue(data?.ai_generated_routine);
        setAiGeneratedRoutine(aiRoutineValue);

        const treatmentEnabled = parseFeatureToggle(data?.service_suggestion ?? data?.treatment);
        const productEnabled = parseFeatureToggle(data?.product_suggestion ?? data?.product);
        setServiceSuggestionEnabled(treatmentEnabled);
        setProductSuggestionEnabled(productEnabled);

        const segmentationMap = data?.segmentation && typeof data.segmentation === 'object' ? data.segmentation : null;
        let advancedChartsEnabled = segmentationMap?.['Advanced graphical charts'] != null
          ? parseFeatureToggle(segmentationMap['Advanced graphical charts'])
          : true;
        let skinConcernsEnabled = segmentationMap?.['Skin Concerns'] != null
          ? parseFeatureToggle(segmentationMap['Skin Concerns'])
          : true;
        let skinConditionsEnabled = segmentationMap?.['Skin Conditions'] != null
          ? parseFeatureToggle(segmentationMap['Skin Conditions'])
          : true;

        const allowed = new Set();
        fields.forEach((field) => {
          const label = String(field?.label || '').trim();
          if (label === 'Advanced graphical charts' && segmentationMap?.['Advanced graphical charts'] == null) {
            advancedChartsEnabled = parseFeatureToggle(field?.key);
          }
          if (label === 'Skin Concerns' && segmentationMap?.['Skin Concerns'] == null) {
            skinConcernsEnabled = parseFeatureToggle(field?.key);
          }
          if (label === 'Skin Conditions' && segmentationMap?.['Skin Conditions'] == null) {
            skinConditionsEnabled = parseFeatureToggle(field?.key);
          }

          if (field?.key !== 'yes' || !Array.isArray(field?.options)) return;
          field.options.forEach((opt) => {
            const normalized = normalizeSegmentationOption(opt);
            if (normalized) allowed.add(normalized);
          });
        });

        setShowAdvancedCharts(advancedChartsEnabled);
        setShowSkinConcerns(skinConcernsEnabled);
        setShowSkinConditions(skinConditionsEnabled);
        setSegmentationAllowedNames(allowed.size > 0 ? allowed : (fields.length > 0 ? new Set() : null));
        setSuggestConfigLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setAiGeneratedSummary(null);
          setSegmentationAiMode(null);
          setAiGeneratedRoutine(null);
          setShowAdvancedCharts(true);
          setShowSkinConcerns(true);
          setShowSkinConditions(true);
          setSegmentationAllowedNames(null);
          setServiceSuggestionEnabled(false);
          setProductSuggestionEnabled(false);
          setSuggestConfigLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  const recommendationRequirements = useMemo(() => {
    return {
      treatmentRequired: serviceSuggestionEnabled,
      productRequired: productSuggestionEnabled,
    };
  }, [serviceSuggestionEnabled, productSuggestionEnabled]);

  const buildRecommendationPayload = useCallback(() => {
    const allDetections = skinResultsFromState?.detections || [];
    const filteredDetections =
      visibleConditionNames != null
        ? allDetections.filter((d) => {
          const name = d.class_name || d.className;
          return name && visibleConditionNames.includes(name);
        })
        : allDetections;
    const detectionsForRecommendation = filteredDetections.length > 0 ? filteredDetections : allDetections;

    const skinScore = skinResultsFromState?.skinScore || {};
    const conditionScores = skinScore?.condition_scores || skinScore?.skin_label_scores || {};
    let detectedConditions = detectionsForRecommendation
      .map((d) => d.class_name || d.className)
      .filter(Boolean);

    // Fallback for sessions where detection list gets filtered to empty but score map exists.
    if (detectedConditions.length === 0 && conditionScores && typeof conditionScores === 'object') {
      detectedConditions = Object.entries(conditionScores)
        .filter(([, score]) => {
          const n = Number(score);
          return Number.isFinite(n) && n < 89;
        })
        .map(([name]) => String(name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
        .filter(Boolean);
    }

    return {
      session_id: sessionId,
      app_session_token: token,
      final_skin_score: skinScore?.final_skin_score || skinScore?.score || 0,
      skin_grade: skinScore?.grade || 'Transitioning',
      condition_scores: conditionScores,
      detected_conditions: detectedConditions,
      user_age: userDataFromState?.age,
      user_skin_type: userDataFromState?.skin_type || userDataFromState?.skinType,
      product_suggest_limit: DEFAULT_PRODUCT_SUGGEST_LIMIT,
    };
  }, [sessionId, token, skinResultsFromState, visibleConditionNames, userDataFromState]);

  const fetchStoredRecommendationsStatus = useCallback(async () => {
    if (!sessionId) return { treatmentReady: false, productReady: false };
    const API_BASE_URL = getApiBaseUrl() || '';
    const status = { treatmentReady: false, productReady: false };

    if (recommendationRequirements.productRequired) {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-products/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}`,
          { timeoutMs: 20000 }
        );
        const data = await response.json().catch(() => ({}));
        if (response.ok && data?.success && hasRecommendedProductsPayload(data?.recommended_products)) {
          status.productReady = true;
        }
      } catch (e) {
      }
    } else {
      status.productReady = true;
    }

    if (recommendationRequirements.treatmentRequired) {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-treatments/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}`,
          { timeoutMs: 20000 }
        );
        const data = await response.json().catch(() => ({}));
        if (response.ok && data?.success && hasRecommendedTreatmentsPayload(data?.recommended_treatments)) {
          status.treatmentReady = true;
        }
      } catch (e) {
      }
    } else {
      status.treatmentReady = true;
    }

    return status;
  }, [recommendationRequirements.productRequired, recommendationRequirements.treatmentRequired, sessionId, token]);

  const fetchStoredTreatments = useCallback(async () => {
    if (!sessionId) {
      setRecommendedTreatments([]);
      return [];
    }
    const API_BASE_URL = getApiBaseUrl() || '';
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-treatments/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}`,
        { timeoutMs: 20000 }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success && Array.isArray(data?.recommended_treatments)) {
        setRecommendedTreatments(data.recommended_treatments);
        return data.recommended_treatments;
      } else {
        setRecommendedTreatments([]);
        return [];
      }
    } catch (e) {
      setRecommendedTreatments([]);
      return [];
    }
  }, [sessionId, token]);

  const fetchStoredProducts = useCallback(async () => {
    if (!sessionId) {
      setRecommendedProducts({ morning: [], evening: [] });
      return { morning: [], evening: [] };
    }
    const API_BASE_URL = getApiBaseUrl() || '';
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-products/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}`,
        { timeoutMs: 20000 }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success && hasRecommendedProductsPayload(data?.recommended_products)) {
        const morning = Array.isArray(data?.recommended_products?.morning) ? data.recommended_products.morning : [];
        const evening = Array.isArray(data?.recommended_products?.evening) ? data.recommended_products.evening : [];
        const productsOut = { morning, evening };
        setRecommendedProducts(productsOut);
        return productsOut;
      } else {
        setRecommendedProducts({ morning: [], evening: [] });
        return { morning: [], evening: [] };
      }
    } catch (e) {
      setRecommendedProducts({ morning: [], evening: [] });
      return { morning: [], evening: [] };
    }
  }, [sessionId, token]);

  const generateTreatmentsDirectly = useCallback(async () => {
    if (!sessionId) return [];

    const API_BASE_URL = getApiBaseUrl() || '';
    const payload = buildRecommendationPayload();

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-treatments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: 60000,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !hasRecommendedTreatmentsPayload(data?.recommended_treatments)) {
        return [];
      }
      const treatmentsOut = Array.isArray(data?.recommended_treatments) ? data.recommended_treatments : [];
      setRecommendedTreatments(treatmentsOut);
      setRecommendationGenerationState((prev) => ({
        ...prev,
        treatmentReady: true,
        error: null,
      }));
      return treatmentsOut;
    } catch {
      return [];
    }
  }, [buildRecommendationPayload, sessionId]);

  const generateProductsDirectly = useCallback(async () => {
    if (!sessionId) return { morning: [], evening: [] };

    const API_BASE_URL = getApiBaseUrl() || '';
    const payload = buildRecommendationPayload();

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: 60000,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !hasRecommendedProductsPayload(data?.recommended_products)) {
        return { morning: [], evening: [] };
      }
      const morning = Array.isArray(data?.recommended_products?.morning) ? data.recommended_products.morning : [];
      const evening = Array.isArray(data?.recommended_products?.evening) ? data.recommended_products.evening : [];
      const productsOut = { morning, evening };
      setRecommendedProducts(productsOut);
      setRecommendationGenerationState((prev) => ({
        ...prev,
        productReady: true,
        error: null,
      }));
      return productsOut;
    } catch {
      return { morning: [], evening: [] };
    }
  }, [buildRecommendationPayload, sessionId]);

  const generateDeepDiveDirectly = useCallback(async () => {
    if (!sessionId || !skinResultsFromState) return {};

    const API_BASE_URL = getApiBaseUrl() || '';
    const allDetections = skinResultsFromState?.detections || [];
    const detectionsForDeepDive =
      visibleConditionNames != null
        ? allDetections.filter((d) => {
          const name = d.class_name || d.className;
          return name && visibleConditionNames.includes(name);
        })
        : allDetections;

    const rawConditions = skinResultsFromState?.skinConditions || null;
    const leanSkinConditions = rawConditions && typeof rawConditions === 'object'
      ? Object.fromEntries(
        Object.entries(rawConditions).map(([k, v]) => {
          if (!v || typeof v !== 'object') return [k, v];
          const { mask, ...rest } = v;
          return [k, rest];
        })
      )
      : rawConditions;

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/generate-analysis-deep-dive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          detections: detectionsForDeepDive,
          skin_score: skinResultsFromState?.skinScore || null,
          previous_skin_data: effectivePreviousSkinData,
          skin_conditions: leanSkinConditions,
          user_data: {
            ...(userDataFromState || {}),
            session_id: sessionId,
          },
          aqi_data: aqiData && aqiData.success ? {
            aqi: aqiData.aqi,
            category: aqiData.category,
            location: aqiData.location,
            coordinates: aqiData.coordinates,
            latitude: aqiData.latitude,
            longitude: aqiData.longitude,
            uv_index: aqiData.uv_index !== undefined && aqiData.uv_index !== null ? aqiData.uv_index : null,
            uv_level: aqiData.uv_level,
            temperature: aqiData.temperature !== undefined && aqiData.temperature !== null ? aqiData.temperature : null,
            humidity: aqiData.humidity !== undefined && aqiData.humidity !== null ? aqiData.humidity : null,
            wind_speed: aqiData.wind_speed !== undefined && aqiData.wind_speed !== null ? aqiData.wind_speed : null,
          } : null,
          session_id: sessionId,
          app_session_token: token,
          use_ollama_for_descriptions: useOllamaForAi,
        }),
        timeoutMs: 60000,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !data?.deep_dive || typeof data.deep_dive !== 'object') {
        return {};
      }
      return normalizeDescriptionMap(data.deep_dive) || {};
    } catch {
      return {};
    }
  }, [aqiData, effectivePreviousSkinData, sessionId, skinResultsFromState, token, userDataFromState, useOllamaForAi, visibleConditionNames]);

  // Rehydrate recommendation readiness from stored backend records.
  // This keeps button gating correct after refresh/navigation when routine data is cached.
  useEffect(() => {
    if (!suggestConfigLoaded || !sessionId) return;
    let cancelled = false;

    const refreshStatus = async () => {
      const status = await fetchStoredRecommendationsStatus();
      if (cancelled) return;
      setRecommendationGenerationState((prev) => ({
        ...prev,
        // Never downgrade once ready; avoids transient API/state gaps disabling buttons after back/forward.
        treatmentReady: prev.treatmentReady || status.treatmentReady,
        productReady: prev.productReady || status.productReady,
      }));
    };

    refreshStatus();
    return () => {
      cancelled = true;
    };
  }, [fetchStoredRecommendationsStatus, sessionId, suggestConfigLoaded]);

  // Hide Summary tab only when session config ai_generated_summary === "no"; flag only controls Ollama vs default content
  const showSummaryAndInsights = aiGeneratedSummary !== 'no';

  // Salon flow: AI routine generation is disabled; hide this block.
  const showRoutine = false;

  // Concerns and conditions are controlled by segmentation payload fetched above.
  const [segmentationAllowedNames, setSegmentationAllowedNames] = useState(null);

  // Helper function to get UV Index color based on value
  const getUvIndexColor = (uvIndex) => {
    if (uvIndex <= 2) return '#00E400'; // Low - Green
    if (uvIndex <= 5) return '#FFFF00'; // Moderate - Yellow
    if (uvIndex <= 7) return '#FF7E00'; // High - Orange
    if (uvIndex <= 10) return '#FF0000'; // Very High - Red
    return '#8F3F97'; // Extreme - Purple
  };

  const getUvDescription = (uvIndex) => {
    if (uvIndex == null || uvIndex === undefined) return 'N/A';
    const n = Number(uvIndex);
    if (n <= 2) return 'Low';
    if (n <= 5) return 'Moderate';
    if (n <= 7) return 'High';
    if (n <= 10) return 'Very High';
    return 'Extreme';
  };

  // Fetch AQI data (only once per session) - Skip if already in state
  useEffect(() => {
    // Skip if AQI data already exists in state or has been fetched
    if (aqiDataFromState && aqiDataFromState.success) {
      setAqiData(aqiDataFromState);
      hasFetchedAqiRef.current = true;
      setAqiLoading(false);
      return;
    }

    if (hasFetchedAqiRef.current || aqiData) {
      setAqiLoading(false);
      return;
    }

    const fetchAQI = async () => {
      // Only fetch if geolocation is supported
      if (!navigator.geolocation) {
        setAqiLoading(false);
        return;
      }

      setAqiLoading(true);
      const API_BASE_URL = getApiBaseUrl() || '';

      try {
        // Get user's location
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        });

        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        // Fetch AQI data from backend
        const response = await fetchWithTimeout(
          `${API_BASE_URL}${SALON_API_PREFIX}/aqi?latitude=${latitude}&longitude=${longitude}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
            ,
            timeoutMs: 20000,
          }
        );

        const data = await response.json().catch(() => ({}));

        if (response.ok && data.success) {
          setAqiData(data);
          hasFetchedAqiRef.current = true;
          // Store AQI data in localStorage with session ID
          if (sessionId) {
            try {
              localStorage.setItem(`saloon_aqi_${sessionId}`, JSON.stringify(data));
            } catch (e) {
            }
          }
          // Save AQI data (including temperature) to database
          if (sessionId && data.success) {
            try {
              await saveAqiUpdate(sessionId, data, token || null);
            } catch (error) {
              console.warn('Failed to save AQI data to database:', error);
            }
          }
        }
      } catch (error) {
        // Silently fail - AQI is optional
      } finally {
        setAqiLoading(false);
      }
    };

    fetchAQI();
  }, [sessionId, aqiData, aqiDataFromState, token]);

  useEffect(() => {
    if (!serviceSuggestionEnabled) {
      setHasCompletedTreatmentsStep(true);
    }
  }, [serviceSuggestionEnabled]);

  useEffect(() => {
    if (!productSuggestionEnabled) {
      setHasCompletedProductsStep(true);
    }
  }, [productSuggestionEnabled]);

  useEffect(() => {
    if (!showSummaryAndInsights) {
      setHasCompletedSummaryStep(true);
    }
  }, [showSummaryAndInsights]);

  // Ordered populate flow:
  // 1) concern preview default (already immediate)
  // 2) recommended treatments
  // 3) recommended products
  // 4) analysis deep dive
  // 5) AI summary
  // 6) AI routine in background
  useEffect(() => {
    if (hasGeneratedRef.current) {
      return;
    }
    if (!suggestConfigLoaded) {
      return;
    }
    if (recommendGenerationStartedRef.current) {
      return;
    }

    const initializeOrderedPopulation = async () => {
      if (!skinResultsFromState) {
        setError('Missing required data');
        setIsLoading(false);
        return;
      }

      recommendGenerationStartedRef.current = true;
      setError(null);
      setRecommendationGenerationState((prev) => ({ ...prev, error: null }));

      const loadTreatments = async () => {
        if (!serviceSuggestionEnabled) return [];
        setIsTreatmentsLoading(true);
        try {
          let treatmentsNow = await fetchStoredTreatments();
          if (!hasRecommendedTreatmentsPayload(treatmentsNow)) {
            treatmentsNow = await generateTreatmentsDirectly();
            // Persist can fail (e.g. id sequence) — keep generated list if stored is empty.
            if (hasRecommendedTreatmentsPayload(treatmentsNow)) {
              const stored = await fetchStoredTreatments();
              if (hasRecommendedTreatmentsPayload(stored)) {
                treatmentsNow = stored;
              } else {
                setRecommendedTreatments(treatmentsNow);
              }
            }
          }
          return treatmentsNow;
        } finally {
          setIsTreatmentsLoading(false);
        }
      };

      const loadProducts = async () => {
        if (!productSuggestionEnabled) return { morning: [], evening: [] };
        setIsProductsLoading(true);
        try {
          let productsNow = await fetchStoredProducts();
          if (!hasRecommendedProductsPayload(productsNow)) {
            productsNow = await generateProductsDirectly();
            if (hasRecommendedProductsPayload(productsNow)) {
              const stored = await fetchStoredProducts();
              if (hasRecommendedProductsPayload(stored)) {
                productsNow = stored;
              } else {
                setRecommendedProducts(productsNow);
              }
            }
          }
          return productsNow;
        } finally {
          setIsProductsLoading(false);
        }
      };

      const [treatmentsNow, productsNow] = await Promise.all([
        loadTreatments(),
        loadProducts(),
      ]);

      const treatmentOk = !serviceSuggestionEnabled || hasRecommendedTreatmentsPayload(treatmentsNow);
      const productOk = !productSuggestionEnabled || hasRecommendedProductsPayload(productsNow);
      const recErrors = [];
      if (serviceSuggestionEnabled && !treatmentOk) {
        recErrors.push('Treatment recommendations could not be generated.');
      }
      if (productSuggestionEnabled && !productOk) {
        recErrors.push('Product recommendations could not be generated.');
      }

      setRecommendationGenerationState((prev) => ({
        ...prev,
        treatmentReady: treatmentOk || prev.treatmentReady,
        productReady: productOk || prev.productReady,
        error: recErrors.length ? recErrors.join(' ') : null,
      }));
      if (recErrors.length) {
        setError(recErrors.join(' '));
      }
      setHasCompletedTreatmentsStep(true);
      setHasCompletedProductsStep(true);

      // Step 4: Condition descriptions / deep dive
      if (showAdvancedCharts) {
        setIsLoadingConditionDescriptions(true);
        const deepDiveDescriptions = await generateDeepDiveDirectly();
        if (deepDiveDescriptions && Object.keys(deepDiveDescriptions).length > 0) {
          setAiConditionDescriptions((prev) => {
            const merged = normalizeDescriptionMap({ ...(prev || {}), ...deepDiveDescriptions }) || {};
            const current = getSkinFlowState(sessionId) || {};
            saveSkinFlowState(sessionId, {
              ...current,
              sessionId,
              aiConditionDescriptions: merged,
            });
            return merged;
          });
        }
        setIsLoadingConditionDescriptions(false);
      } else if (useOllamaForAi && sessionId) {
        setIsLoadingConditionDescriptions(true);
        try {
          const storedDescriptions = normalizeDescriptionMap(await fetchStoredConditionDescriptions({
            sessionId,
            appSessionToken: token || null,
            generateIfMissing: true,
            previousSkinData: effectivePreviousSkinData,
          }));
          if (storedDescriptions && Object.keys(storedDescriptions).length > 0) {
            setAiConditionDescriptions((prev) => {
              const merged = normalizeDescriptionMap({ ...(prev || {}), ...storedDescriptions }) || {};
              const current = getSkinFlowState(sessionId) || {};
              saveSkinFlowState(sessionId, {
                ...current,
                sessionId,
                aiConditionDescriptions: merged,
              });
              return merged;
            });
          }
        } catch {
          // Non-blocking; static fallbacks remain available.
        }
        setIsLoadingConditionDescriptions(false);
      }
      setHasCompletedDeepDiveStep(true);

      // Step 5: Summary
      if (showSummaryAndInsights && (!summary?.trim() || forceRegenerateForNewScan)) {
        setSummaryLoading(true);
        try {
          const API_BASE_URL = getApiBaseUrl() || '';
          const useOllamaSummary = useOllamaForAi;
          const allDetections = skinResultsFromState?.detections || [];
          const detectionsForSummary =
            visibleConditionNames != null
              ? allDetections.filter((d) => {
                const name = d.class_name || d.className;
                return name && visibleConditionNames.includes(name);
              })
              : allDetections;
          const rawConditions = skinResultsFromState?.skinConditions || null;
          const leanSkinConditions = rawConditions && typeof rawConditions === 'object'
            ? Object.fromEntries(
              Object.entries(rawConditions).map(([k, v]) => {
                if (!v || typeof v !== 'object') return [k, v];
                const { mask, ...rest } = v;
                return [k, rest];
              })
            )
            : rawConditions;

          const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/generate-routine`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              detections: detectionsForSummary,
              skin_score: skinResultsFromState?.skinScore || null,
              previous_skin_data: effectivePreviousSkinData,
              skin_conditions: leanSkinConditions,
              user_data: {
                ...(userDataFromState || {}),
                session_id: sessionId,
              },
              aqi_data: aqiData && aqiData.success ? {
                aqi: aqiData.aqi,
                category: aqiData.category,
                location: aqiData.location,
                coordinates: aqiData.coordinates,
                latitude: aqiData.latitude,
                longitude: aqiData.longitude,
                uv_index: aqiData.uv_index !== undefined && aqiData.uv_index !== null ? aqiData.uv_index : null,
                uv_level: aqiData.uv_level,
                temperature: aqiData.temperature !== undefined && aqiData.temperature !== null ? aqiData.temperature : null,
                humidity: aqiData.humidity !== undefined && aqiData.humidity !== null ? aqiData.humidity : null,
                wind_speed: aqiData.wind_speed !== undefined && aqiData.wind_speed !== null ? aqiData.wind_speed : null,
              } : null,
              session_id: sessionId,
              app_session_token: token,
              ai_generated_summary: useOllamaSummary,
              use_ollama_for_summary: useOllamaSummary,
              use_ollama_for_routine: useOllamaSummary,
              captured_image: capturedImageFromState || skinResultsFromState?.markedImage || null,
              force_regenerate: forceRegenerateForNewScan,
            }),
            timeoutMs: 60000,
          });

          const data = await response.json().catch(() => ({}));
          if (response.ok && data?.success && typeof data?.summary === 'string' && data.summary.trim()) {
            setSummary(data.summary);
            setShowFallbackSummary(false);
          } else {
            setShowFallbackSummary(true);
          }
        } catch (err) {
          setShowFallbackSummary(true);
        } finally {
          setSummaryLoading(false);
        }
      }
      setHasCompletedSummaryStep(true);

      // Step 6: routine in background (non-blocking)
      setIsRoutineBackgroundRunning(true);
      void (async () => {
        try {
          const API_BASE_URL = getApiBaseUrl() || '';
          await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/generate-routine`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              detections: skinResultsFromState?.detections || [],
              skin_score: skinResultsFromState?.skinScore || null,
              previous_skin_data: effectivePreviousSkinData,
              skin_conditions: skinResultsFromState?.skinConditions || null,
              user_data: {
                ...(userDataFromState || {}),
                session_id: sessionId,
              },
              aqi_data: aqiData && aqiData.success ? aqiData : null,
              session_id: sessionId,
              app_session_token: token,
              ai_generated_summary: false,
              use_ollama_for_summary: false,
              use_ollama_for_routine: true,
              captured_image: capturedImageFromState || skinResultsFromState?.markedImage || null,
              force_regenerate: false,
            }),
            timeoutMs: 60000,
          });
        } catch {
          // Routine runs in background, so failure here should not block UI.
        } finally {
          setIsRoutineBackgroundRunning(false);
        }
      })();

      hasGeneratedRef.current = true;
      setIsLoading(false);
    };

    initializeOrderedPopulation();
  }, [
    aqiData,
    useOllamaForAi,
    capturedImageFromState,
    fetchStoredProducts,
    fetchStoredTreatments,
    forceRegenerateForNewScan,
    generateDeepDiveDirectly,
    generateProductsDirectly,
    generateTreatmentsDirectly,
    effectivePreviousSkinData,
    serviceSuggestionEnabled,
    showAdvancedCharts,
    productSuggestionEnabled,
    sessionId,
    showSummaryAndInsights,
    skinResultsFromState,
    suggestConfigLoaded,
    summary,
    token,
    userDataFromState,
    visibleConditionNames,
    recommendationRetryTick,
  ]);

  const handleRetryRecommendations = useCallback(() => {
    hasGeneratedRef.current = false;
    recommendGenerationStartedRef.current = false;
    setError(null);
    setRecommendationGenerationState((prev) => ({ ...prev, error: null }));
    setIsLoading(true);
    setRecommendationRetryTick((tick) => tick + 1);
  }, []);

  const handleCloseUspPopup = () => setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });

  const isAiContentLoading = summaryLoading;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) handleCloseUspPopup();
    };
    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [uspPopup.open]);

  const handleBack = () => {
    navigate(pathWithSessionId('/skin-analysis', sessionId), {
      state: {
        capturedImage: capturedImageFromState,
        userData: userDataFromState,
        sessionId: sessionId,
        skinResults: skinResultsFromState,
        aqiData: aqiData,
        aiConditionDescriptions,
        ...(token && { token }),
      }
    });
  };

  const handleHome = async () => {
    const API_BASE_URL = getApiBaseUrl() || '';
    if (sessionId) {
      await finalizeCustomerSession(API_BASE_URL, sessionId, token || null);
    }
    navigate(sessionLandingPath);
  };

  const handleAddTreatmentToCart = (treatment, quantity, cartKey) => {
    setTreatmentCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[cartKey];
        return next;
      }
      return { ...prev, [cartKey]: { treatment, quantity } };
    });
  };

  const handleAddProductToCart = (product, quantity, productId) => {
    setProductCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return {
        ...prev,
        [productId]: {
          product,
          quantity,
        },
      };
    });
  };

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalProductCartItems = () =>
    Object.values(productCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalCombinedCartItems = () => getTotalTreatmentCartItems() + getTotalProductCartItems();

  const handleOpenCheckoutModal = () => setIsCheckoutModalOpen(true);
  const handleCloseCheckoutModal = () => setIsCheckoutModalOpen(false);
  const handleOpenHomeConfirmPopup = () => setShowHomeConfirmPopup(true);
  const handleCloseHomeConfirmPopup = () => setShowHomeConfirmPopup(false);
  const handleConfirmGoHome = () => {
    setShowHomeConfirmPopup(false);
    navigate(sessionLandingPath);
  };
  const handleTreatmentCartUpdate = (updatedMap) => setTreatmentCartItems(updatedMap || {});
  const handleProductCartUpdate = (updatedMap) => setProductCartItems(updatedMap || {});

  const saveProductCartToBackend = useCallback((items) => {
    const baseUrl = getApiBaseUrl() || '';
    if (!baseUrl || !sessionId) return;
    persistProductCart(baseUrl, sessionId, token, items);
  }, [sessionId, token]);

  const saveTreatmentCartToBackend = useCallback((items) => {
    const baseUrl = getApiBaseUrl() || '';
    if (!baseUrl || !sessionId) return;
    persistTreatmentCart(baseUrl, sessionId, token, items);
  }, [sessionId, token]);

  useEffect(() => {
    saveProductCartToBackend(productCartItems);
  }, [productCartItems, saveProductCartToBackend]);

  useEffect(() => {
    saveTreatmentCartToBackend(treatmentCartItems);
  }, [treatmentCartItems, saveTreatmentCartToBackend]);


  const normalizeWhyRecommendedText = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/\(\s*score\s*:\s*(\d{1,3})(?:\s*\/\s*100)?\s*\)/gi, '')
      .replace(/\bscore\s*[:=]?\s*(\d{1,3})(?:\s*\/\s*100|\s*%)?\b/gi, '')
      .trim();
  };

  const getRecommendationLine = (item) => {
    const conditionsToShow = item?.suggested_for_conditions?.length
      ? item.suggested_for_conditions
      : item?.conditions || [];
    if (!conditionsToShow.length) {
      return item?.suggested_for_wording ? normalizeWhyRecommendedText(item.suggested_for_wording) : '';
    }

    const parts = conditionsToShow.map((c) => c);
    return 'Recommended for ' + parts.join(', ');
  };

  const toNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const RUPEE_SYMBOL = '\u20B9';

  const formatPrice = (amount) => {
    if (amount == null || amount === '') return '-';
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount);
    return `${RUPEE_SYMBOL}${n.toFixed(0)}`;
  };

  const hasDiscount = (item) => {
    if (toNumberOrNull(item?.net_price) == null) return false;
    const d = item?.discount;
    if (d == null || d === '') return false;
    const n = Number(d);
    return !Number.isNaN(n) && n > 0;
  };

  const formatDiscountBadge = (item) => {
    const d = Number(item?.discount);
    if (Number.isNaN(d) || d <= 0) return null;
    if (d > 0 && d <= 1) return `${Math.round(d * 100)}% OFF`;
    if (d > 1 && d <= 100) return `${Math.round(d)}% OFF`;
    return `${RUPEE_SYMBOL}${Math.round(d)} OFF`;
  };

  const isVercelBlobUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      const u = new URL(url);
      const host = (u.hostname || '').toLowerCase();
      return (
        u.protocol === 'https:' &&
        (host === 'blob.vercel-storage.com' ||
          host.endsWith('.blob.vercel-storage.com') ||
          host === 'public.blob.vercel-storage.com' ||
          host.endsWith('.public.blob.vercel-storage.com'))
      );
    } catch {
      return false;
    }
  };

  const resolveTreatmentImageUrl = (url) => {
    if (!url) return null;
    let raw = url;
    if (Array.isArray(raw)) raw = raw[0];
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed[0]) raw = parsed[0];
      } catch {
      }
    }
    if (!raw || typeof raw !== 'string') return null;
    if (!isVercelBlobUrl(raw)) return raw;
    const base = (getApiBaseUrl() || '').replace(/\/$/, '');
    return `${base}${SALON_API_PREFIX}/blob-proxy?url=${encodeURIComponent(raw)}`;
  };

  const resolveProductImageUrl = (url) => resolveTreatmentImageUrl(url);

  // Get user info
  const userName = userDataFromState?.name || 'User';
  const skinType = userDataFromState?.skinType || userDataFromState?.skin_type || 'Not specified';
  const gender = userDataFromState?.gender || 'Not specified';
  const userImage = capturedImageFromState || skinResultsFromState?.markedImage || null;
  const previewBaseImage = capturedImageFromState || skinResultsFromState?.capturedImage || userImage || null;
  const profileSubtitle = `${gender} - ${skinType}`;
  const overallSkinScoreRaw = skinResultsFromState?.skinScore?.final_skin_score ?? skinResultsFromState?.skinScore?.score ?? null;
  const overallSkinScore = overallSkinScoreRaw != null && !Number.isNaN(Number(overallSkinScoreRaw))
    ? Math.max(0, Math.min(100, Math.round(Number(overallSkinScoreRaw))))
    : null;
  const previousOverallSkinScore = useMemo(() => {
    const prevRaw = previousSkinData?.skinScore?.final_skin_score
      ?? previousSkinData?.skinScore?.score
      ?? previousSkinData?.skin_score?.final_skin_score
      ?? previousSkinData?.skin_score?.score
      ?? previousSkinData?.skinScore
      ?? previousSkinData?.skin_score
      ?? null;
    if (prevRaw == null || Number.isNaN(Number(prevRaw))) return null;
    return Math.max(0, Math.min(100, Math.round(Number(prevRaw))));
  }, [previousSkinData]);
  const analysisDateValue = useMemo(() => {
    const rawDate = effectiveState?.analysisDate
      || effectiveState?.analysis_date
      || skinResultsFromState?.analysis_date
      || skinResultsFromState?.created_at
      || effectiveState?.createdAt
      || null;
    const parsed = rawDate ? new Date(rawDate) : new Date();
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [effectiveState, skinResultsFromState]);
  const analysisDateText = useMemo(() => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(analysisDateValue);
  }, [analysisDateValue]);
  const previousAnalysisDateValue = useMemo(() => {
    const rawDate = previousSkinData?.created_at || previousSkinData?.analysis_date || null;
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, [previousSkinData]);
  const previousAnalysisDateText = useMemo(() => {
    if (!previousAnalysisDateValue) return null;
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(previousAnalysisDateValue);
  }, [previousAnalysisDateValue]);
  const radarDaysBetween = useMemo(() => {
    if (!previousAnalysisDateValue || !analysisDateValue) return null;
    const diffMs = analysisDateValue.getTime() - previousAnalysisDateValue.getTime();
    return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
  }, [analysisDateValue, previousAnalysisDateValue]);

  useEffect(() => {
    if (overallSkinScore == null) return;
    const canvas = skinGaugeCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cx = canvas.width / 2;
    const cy = 210;
    const radius = 120;
    const thickness = 30;

    const scoreMap = {
      critical: { color: '#ff4757', label: 'Critical' },
      poor: { color: '#ffa502', label: 'Poor' },
      fair: { color: '#eccc68', label: 'Fair' },
      good: { color: '#7bed9f', label: 'Good' },
      excellent: { color: '#2ed573', label: 'Great' },
    };

    const segments = [
      { start: 0.90, end: 1.13, key: 'critical' },
      { start: 1.15, end: 1.37, key: 'poor' },
      { start: 1.39, end: 1.61, key: 'fair' },
      { start: 1.63, end: 1.85, key: 'good' },
      { start: 1.87, end: 2.10, key: 'excellent' },
    ];

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    segments.forEach((seg) => {
      const config = scoreMap[seg.key];

      ctx.beginPath();
      ctx.arc(cx, cy, radius, seg.start * Math.PI, seg.end * Math.PI);
      ctx.strokeStyle = config.color;
      ctx.lineWidth = thickness;
      ctx.lineCap = 'butt';
      ctx.stroke();

      const midAngle = ((seg.start + seg.end) / 2) * Math.PI;
      const tx = cx + (radius + 35) * Math.cos(midAngle);
      const ty = cy + (radius + 35) * Math.sin(midAngle);

      ctx.save();
      ctx.translate(tx, ty);
      ctx.rotate(midAngle + Math.PI / 2);
      ctx.fillStyle = '#636e72';
      ctx.font = '600 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(config.label.toUpperCase(), 0, 0);
      ctx.restore();
    });

    const startRad = 0.9;
    const endRad = 2.1;
    const needleRad = (startRad + (overallSkinScore / 100) * (endRad - startRad)) * Math.PI;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(needleRad);
    ctx.shadowBlur = 10;
    ctx.shadowColor = 'rgba(0,0,0,0.1)';

    ctx.beginPath();
    ctx.moveTo(0, -4);
    ctx.lineTo(radius - 5, 0);
    ctx.lineTo(0, 4);
    ctx.fillStyle = '#2d3436';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#2d3436';
    ctx.stroke();
    ctx.restore();
  }, [overallSkinScore]);

  // Concerns split by severity (score < 75) vs good (score >= 75)
  const concernsBySeverity = useMemo(() => {
    const skinScore = skinResultsFromState?.skinScore || {};
    const labelScores = skinScore?.skin_label_scores && typeof skinScore.skin_label_scores === 'object' ? skinScore.skin_label_scores : null;

    // 1) Always include YOLO detections (worst score per class).
    const groupedDetections = (skinResultsFromState?.detections || []).reduce((acc, det) => {
      const raw = det.class_name || det.className || 'Unknown';
      const className = normalizeConditionName(raw);
      if (!className || className === 'Unknown' || className === 'Mole' || det.class_id === 7 || det.class_id === 8) return acc;
      if (!showSkinConcerns && isSkinQualityName(className)) return acc;
      if (!showSkinConditions && !isSkinQualityName(className)) return acc;
      if (segmentationAllowedNames !== null && !segmentationAllowedNames.has(className)) return acc;
      const scoreNum = det.score !== undefined ? Number(det.score) : 100;
      const score = Number.isNaN(scoreNum) ? 100 : scoreNum;
      if (!acc[className] || score < acc[className].score) {
        acc[className] = { class_name: className, score, class_id: det.class_id, confidences: [], count: 0 };
      }
      const confidence = det.confidence || det.confidence_score || 0;
      acc[className].confidences.push(confidence);
      acc[className].count += 1;
      return acc;
    }, {});

    const detectionItems = Object.values(groupedDetections)
      .map((g) => {
        const avgConfidence = (g.confidences || []).length
          ? g.confidences.reduce((s, c) => s + c, 0) / g.confidences.length
          : 1;
        let color = getColorByClassId(g.class_id);
        if (!BGR_COLORS_BY_ID[g.class_id]) color = CLASS_COLORS_MAP[g.class_name] || 'rgb(140, 80, 200)';
        return {
          class_name: g.class_name,
          score: Math.round(g.score),
          confidence: avgConfidence,
          count: g.count,
          color,
          class_id: g.class_id,
          is_condition: false,
        };
      });

    // 2) Include segmentation concerns from skinConditions.
    const conditionMapping = { hydration: 'Hydration', oxygen: 'Oxygen', smoothness: 'Smoothness', dullness: 'Dullness', uneven_skintone: 'UnevenSkin' };
    const conditionItems = [];
    const skinConditions = skinResultsFromState?.skinConditions || {};
    Object.entries(conditionMapping).forEach(([key, displayName]) => {
      if (!showSkinConcerns) return;
      if (segmentationAllowedNames !== null && !segmentationAllowedNames.has(displayName)) return;
      const v = skinConditions[key]?.score;
      const n = v != null ? Number(v) : null;
      if (n == null || Number.isNaN(n)) return;
      conditionItems.push({
        class_name: displayName,
        score: Math.round(n),
        confidence: 1,
        count: 1,
        color: CLASS_COLORS_MAP[displayName] || 'rgb(140, 80, 200)',
        class_id: null,
        is_condition: true,
      });
    });

    // 3) Optionally overlay labelScores (if present) for items we already track or extra ones.
    const byName = {};
    [...detectionItems, ...conditionItems].forEach((it) => { byName[it.class_name] = it; });
    if (labelScores) {
      Object.entries(labelScores).forEach(([rawName, rawScore]) => {
        const name = canonicalSkinConditionName(rawName) || normalizeConditionName(rawName);
        const n = rawScore != null ? Number(rawScore) : null;
        if (!name || n == null || Number.isNaN(n)) return;
        if (!showSkinConcerns && isSkinQualityName(name)) return;
        if (!showSkinConditions && !isSkinQualityName(name)) return;
        if (segmentationAllowedNames !== null && !segmentationAllowedNames.has(name)) return;
        const existing = byName[name];
        if (existing) {
          existing.score = Math.round(Math.min(existing.score, n));
          return;
        }
        byName[name] = {
          class_name: name,
          score: Math.round(n),
          confidence: 1,
          count: 1,
          color: CLASS_COLORS_MAP[name] || 'rgb(140, 80, 200)',
          class_id: null,
          is_condition: isSkinQualityName(name),
        };
      });
    }

    let allItems = Object.values(byName).sort((a, b) => a.score - b.score);

    // Match Results page insight cards when that list is provided (prevents mismatch between pages).
    if (Array.isArray(visibleConditionNames) && visibleConditionNames.length > 0) {
      const allowed = new Set(visibleConditionNames);
      allItems = allItems.filter((d) => allowed.has(d.class_name));
    }
    const severity = allItems.filter(d => d.score < 75);
    const good = allItems.filter(d => d.score >= 75);
    return { severity, good, allItems };
  }, [skinResultsFromState, segmentationAllowedNames, visibleConditionNames, showSkinConcerns, showSkinConditions]);

  const concernRadarItems = useMemo(() => {
    return [...concernsBySeverity.allItems]
      .sort((a, b) => a.score - b.score)
      .map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
        // Use score directly: bigger polygon area = better health score
        intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
      }));
  }, [concernsBySeverity.allItems]);

  const radarModel = useMemo(() => {
    if (!concernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;
    const axis = concernRadarItems.map((item, idx) => {
      const angle = ((Math.PI * 2 * idx) / concernRadarItems.length) - Math.PI / 2;
      const dataRadius = (item.intensity / 100) * radius;
      const pointX = cx + Math.cos(angle) * dataRadius;
      const pointY = cy + Math.sin(angle) * dataRadius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const labelX = cx + Math.cos(angle) * (radius + 14);
      const labelY = cy + Math.sin(angle) * (radius + 14);
      return {
        ...item,
        angle,
        pointX,
        pointY,
        labelX,
        labelY,
        labelAnchor,
        labelOffsetX,
      };
    });
    const polygon = axis.map((a) => `${a.pointX},${a.pointY}`).join(' ');
    return { size, cx, cy, radius, axis, polygon, levels: [20, 40, 60, 80, 100] };
  }, [concernRadarItems]);

  const previousConcernRadarItems = useMemo(() => {
    const prevScoreObj = previousSkinData?.skinScore ?? previousSkinData?.skin_score;
    const prevLabelScores = prevScoreObj?.skin_label_scores ?? previousSkinData?.skin_label_scores ?? null;
    if (!prevLabelScores || typeof prevLabelScores !== 'object') return [];
    const items = Object.entries(prevLabelScores)
      .map(([name, score]) => ({
        class_name: String(name || ''),
        score: Math.round(typeof score === 'number' ? score : parseFloat(score) || 0),
        color: CLASS_COLORS_MAP[String(name || '')] || CLASS_COLORS_MAP[getConditionDisplayName(String(name || ''))] || 'rgb(15, 118, 110)',
      }))
      .filter((item) => item.class_name)
      .sort((a, b) => a.score - b.score)
      .map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
        intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
      }));
    return items;
  }, [previousSkinData]);

  const previousRadarModel = useMemo(() => {
    if (!previousConcernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;
    const axis = previousConcernRadarItems.map((item, idx) => {
      const angle = ((Math.PI * 2 * idx) / previousConcernRadarItems.length) - Math.PI / 2;
      const dataRadius = (item.intensity / 100) * radius;
      const pointX = cx + Math.cos(angle) * dataRadius;
      const pointY = cy + Math.sin(angle) * dataRadius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const labelX = cx + Math.cos(angle) * (radius + 14);
      const labelY = cy + Math.sin(angle) * (radius + 14);
      return {
        ...item,
        angle,
        pointX,
        pointY,
        labelX,
        labelY,
        labelAnchor,
        labelOffsetX,
      };
    });
    const polygon = axis.map((a) => `${a.pointX},${a.pointY}`).join(' ');
    return { size, cx, cy, radius, axis, polygon, levels: [20, 40, 60, 80, 100] };
  }, [previousConcernRadarItems]);

  const genericSummaryFromValues = useMemo(() => {
    const score = Number(overallSkinScore || 0);
    const boundedScore = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : null;
    const weakest = [...concernRadarItems].sort((a, b) => a.score - b.score).slice(0, 2);
    const weakText = weakest.length
      ? weakest.map((item) => `${item.label} (${getScoreTone(item.score)})`).join(' and ')
      : 'overall skin balance';
    const scoreText = boundedScore == null
      ? 'Your current skin scan is complete.'
      : boundedScore < 60
        ? 'Your skin currently needs focused recovery support.'
        : boundedScore < 80
          ? 'Your skin is in a moderate state with good recovery potential.'
          : 'Your skin looks strong and well-maintained.';
    return `${scoreText} Your key focus areas right now are ${weakText}. Follow your personalized routine consistently to see visible progress in upcoming scans.`;
  }, [overallSkinScore, concernRadarItems, getScoreTone]);

  const historyTrackingSummary = useMemo(() => {
    if (!previousConcernRadarItems.length || !concernRadarItems.length) return '';
    const prevMap = new Map(
      previousConcernRadarItems.map((item) => [normalizeConditionName(item.class_name), Number(item.score || 0)])
    );
    const compared = concernRadarItems
      .map((item) => {
        const prev = prevMap.get(normalizeConditionName(item.class_name));
        if (!Number.isFinite(prev)) return null;
        return {
          label: item.label,
          delta: Math.round((Number(item.score || 0) - prev) * 10) / 10,
        };
      })
      .filter(Boolean);
    if (!compared.length) return '';

    const improved = [...compared].filter((item) => item.delta >= 3).sort((a, b) => b.delta - a.delta);
    const declined = [...compared].filter((item) => item.delta <= -3).sort((a, b) => a.delta - b.delta);

    const improvedText = improved.slice(0, 3).map((item) => item.label).join(', ');
    const declinedText = declined.slice(0, 3).map((item) => item.label).join(', ');

    if (improved.length && !declined.length) {
      return `Great progress since your previous scan: ${improvedText}. Keep this momentum going.`;
    }
    if (declined.length && !improved.length) {
      return `Compared with your previous scan, ${declinedText} has dropped. A little extra consistency this week can help you bounce back.`;
    }
    if (improved.length && declined.length) {
      return `Since your previous scan, you improved in ${improvedText}, while ${declinedText} needs attention. This trend is normal and can be balanced with routine consistency.`;
    }
    return 'Compared with your previous scan, your skin profile is mostly stable. Staying consistent will help convert stability into visible improvement.';
  }, [previousConcernRadarItems, concernRadarItems]);

  const effectiveSummaryText = useMemo(() => {
    const base = (typeof summary === 'string' && summary.trim()) ? summary.trim() : genericSummaryFromValues;
    const combined = historyTrackingSummary ? `${base}\n\n${historyTrackingSummary}` : base;
    return replaceScoreMentionsWithFriendlyWords(combined);
  }, [summary, genericSummaryFromValues, historyTrackingSummary, replaceScoreMentionsWithFriendlyWords]);

  const radarComparisonModel = useMemo(() => {
    if (!previousConcernRadarItems.length || !concernRadarItems.length) return null;
    const size = 380;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 108;

    const afterMap = new Map(
      concernRadarItems.map((item) => [normalizeConditionName(item.class_name), item])
    );
    const beforeMap = new Map(
      previousConcernRadarItems.map((item) => [normalizeConditionName(item.class_name), item])
    );
    const orderedKeys = [
      ...Array.from(afterMap.keys()),
      ...Array.from(beforeMap.keys()).filter((key) => !afterMap.has(key)),
    ];

    const axis = orderedKeys.map((key, idx) => {
      const afterItem = afterMap.get(key) || null;
      const beforeItem = beforeMap.get(key) || null;
      const angle = ((Math.PI * 2 * idx) / orderedKeys.length) - Math.PI / 2;
      const afterIntensity = afterItem ? Math.max(5, Math.min(100, Number(afterItem.score || 0))) : 0;
      const beforeIntensity = beforeItem ? Math.max(5, Math.min(100, Number(beforeItem.score || 0))) : 0;
      const afterRadius = (afterIntensity / 100) * radius;
      const beforeRadius = (beforeIntensity / 100) * radius;
      const cos = Math.cos(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
      const beforeScore = beforeItem ? Math.round(Number(beforeItem.score || 0)) : null;
      const afterScore = afterItem ? Math.round(Number(afterItem.score || 0)) : null;
      const delta = Number.isFinite(beforeScore) && Number.isFinite(afterScore)
        ? Math.round((afterScore - beforeScore) * 10) / 10
        : null;
      const changeKind = !beforeItem && afterItem
        ? 'new'
        : beforeItem && !afterItem
          ? 'missing'
          : Number.isFinite(delta)
            ? delta >= 3
              ? 'improved'
              : delta <= -3
                ? 'declined'
                : 'stable'
            : 'stable';

      // All labels at equal distance from radar edge
      const labelDistance = 18;

      return {
        key,
        label: getConditionDisplayName(afterItem?.class_name || beforeItem?.class_name || key),
        angle,
        hasAfter: Boolean(afterItem),
        hasBefore: Boolean(beforeItem),
        afterScore,
        beforeScore,
        delta,
        changeKind,
        afterPointX: cx + Math.cos(angle) * afterRadius,
        afterPointY: cy + Math.sin(angle) * afterRadius,
        beforePointX: cx + Math.cos(angle) * beforeRadius,
        beforePointY: cy + Math.sin(angle) * beforeRadius,
        labelX: cx + Math.cos(angle) * (radius + labelDistance),
        labelY: cy + Math.sin(angle) * (radius + labelDistance),
        labelAnchor,
        labelOffsetX,
      };
    });

    return {
      size,
      cx,
      cy,
      radius,
      levels: [20, 40, 60, 80, 100],
      axis,
      afterPolygon: axis.map((a) => `${a.afterPointX},${a.afterPointY}`).join(' '),
      beforePolygon: axis.map((a) => `${a.beforePointX},${a.beforePointY}`).join(' '),
    };
  }, [concernRadarItems, previousConcernRadarItems]);

  const hasBeforeAfterRadar = Boolean(radarComparisonModel && previousRadarModel && radarModel);
  const isAfterComparisonView = hasBeforeAfterRadar && radarComparisonTab === 'after';
  const activeRadarModel = hasBeforeAfterRadar
    ? (radarComparisonTab === 'before' ? previousRadarModel : radarComparisonModel)
    : radarModel;
  const activeRadarScore = hasBeforeAfterRadar && radarComparisonTab === 'before' ? previousOverallSkinScore : overallSkinScore;
  const activeRadarDate = hasBeforeAfterRadar && radarComparisonTab === 'before' ? previousAnalysisDateText : analysisDateText;
  const activeRadarConcernDetails = useMemo(() => {
    if (!activeRadarModel?.axis?.length) return [];
    if (isAfterComparisonView) {
      return activeRadarModel.axis.map((item, idx) => ({
        idx,
        label: item.label,
        beforeScore: item.beforeScore,
        afterScore: item.afterScore,
        delta: item.delta,
        changeKind: item.changeKind || 'stable',
      }));
    }
    return activeRadarModel.axis.map((item, idx) => ({
      idx,
      label: item.label,
      beforeScore: hasBeforeAfterRadar ? item.score : null,
      afterScore: hasBeforeAfterRadar ? null : item.score,
      delta: null,
      changeKind: 'stable',
    }));
  }, [activeRadarModel, hasBeforeAfterRadar, isAfterComparisonView]);

  const activeRadarConcernDetail = useMemo(() => {
    if (!activeRadarConcernDetails.length) return null;
    const safeIndex = Math.max(0, Math.min(radarConcernModal.index, activeRadarConcernDetails.length - 1));
    return activeRadarConcernDetails[safeIndex] || null;
  }, [activeRadarConcernDetails, radarConcernModal.index]);

  const openRadarConcernModal = useCallback((index) => {
    if (!activeRadarConcernDetails.length) return;
    const safeIndex = Math.max(0, Math.min(index, activeRadarConcernDetails.length - 1));
    setRadarConcernModal({ open: true, index: safeIndex });
  }, [activeRadarConcernDetails]);

  const closeRadarConcernModal = useCallback(() => {
    setRadarConcernModal((prev) => ({ ...prev, open: false }));
  }, []);

  const stepRadarConcernModal = useCallback((step) => {
    setRadarConcernModal((prev) => {
      const total = activeRadarConcernDetails.length;
      if (!total) return prev;
      const nextIndex = (prev.index + step + total) % total;
      return { ...prev, index: nextIndex };
    });
  }, [activeRadarConcernDetails.length]);

  const deepDiveLines = useMemo(() => {
    if (!concernsBySeverity.severity.length) return [];

    // Show deep dive only for high/moderate concerns (score < 75) and use AI/stored text as-is.
    return concernsBySeverity.severity
      .map((item) => {
        const label = getConditionDisplayName(item.class_name);
        const descriptionKey = Object.keys(aiConditionDescriptions || {}).find((key) => {
          if (!key) return false;
          const normalizedKey = normalizeConditionName(key);
          const normalizedClassName = normalizeConditionName(item.class_name);
          return normalizedKey === normalizedClassName || key.toLowerCase() === label.toLowerCase();
        });
        const aiDescription = descriptionKey ? aiConditionDescriptions[descriptionKey] : null;
        if (typeof aiDescription !== 'string' || !aiDescription.trim()) return null;
        return `${label}: ${replaceScoreMentionsWithFriendlyWords(aiDescription.trim())}`;
      })
      .filter(Boolean);
  }, [concernsBySeverity.severity, aiConditionDescriptions, replaceScoreMentionsWithFriendlyWords]);

  const detailedConcernItems = useMemo(() => {
    return [...concernsBySeverity.allItems]
      .sort((a, b) => a.score - b.score)
      .map((item) => ({
        ...item,
        label: getConditionDisplayName(item.class_name),
      }));
  }, [concernsBySeverity.allItems]);

  const selectedConcernItem = useMemo(() => {
    if (!selectedConcernName) return null;
    return detailedConcernItems.find((item) => item.class_name === selectedConcernName) || null;
  }, [detailedConcernItems, selectedConcernName]);

  useEffect(() => {
    const score = selectedConcernItem?.score;
    if (score == null) return;

    const drawConcernGauge = (canvas) => {
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const cx = 200;
      const cy = 200;
      const outerRadius = 150;
      const innerRadius = 115;
      const totalSegments = 20;
      const gap = 0.04;

      const getSegmentColor = (segmentValue) => {
        if (segmentValue >= 0 && segmentValue <= 39) return '#ff4757';
        if (segmentValue >= 40 && segmentValue <= 59) return '#ffa502';
        if (segmentValue >= 60 && segmentValue <= 74) return '#eccc68';
        if (segmentValue >= 75 && segmentValue <= 89) return '#7bed9f';
        return '#2ed573';
      };

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < totalSegments; i += 1) {
        const segmentValue = (i / totalSegments) * 100;
        const startAngle = Math.PI + (i * (Math.PI / totalSegments)) + gap;
        const endAngle = Math.PI + ((i + 1) * (Math.PI / totalSegments)) - gap;

        ctx.beginPath();
        ctx.arc(cx, cy, outerRadius, startAngle, endAngle);
        ctx.arc(cx, cy, innerRadius, endAngle, startAngle, true);
        ctx.closePath();
        ctx.fillStyle = segmentValue <= score ? getSegmentColor(segmentValue) : '#f1f2f6';
        ctx.fill();
      }

      const needleAngle = Math.PI + (score / 100) * Math.PI;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(needleAngle);

      ctx.beginPath();
      ctx.moveTo(0, -4);
      ctx.lineTo(innerRadius - 10, 0);
      ctx.lineTo(0, 4);
      ctx.fillStyle = '#2d3436';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(0, 0, 12, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = '#2d3436';
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.restore();
    };

    drawConcernGauge(concernGaugeCopyCanvasRef.current);
    drawConcernGauge(concernGaugeOverlayCanvasRef.current);
  }, [selectedConcernItem]);

  const selectedConcernDescription = useMemo(() => {
    if (!selectedConcernItem) return '';
    const className = selectedConcernItem.class_name;
    const descriptionKey = Object.keys(aiConditionDescriptions || {}).find((key) => {
      if (!key) return false;
      const normalizedKey = normalizeConditionName(key);
      const normalizedClassName = normalizeConditionName(className);
      return normalizedKey === normalizedClassName || key.toLowerCase() === className.toLowerCase();
    });
    const aiDescription = descriptionKey ? aiConditionDescriptions[descriptionKey] : null;
    const source = (typeof aiDescription === 'string' && aiDescription.trim())
      ? aiDescription.trim()
      : (getConditionDescription(className) || '').trim();
    if (!source) return '';
    return replaceScoreMentionsWithFriendlyWords(source);
  }, [selectedConcernItem, aiConditionDescriptions, replaceScoreMentionsWithFriendlyWords]);

  const availableConcernViews = useMemo(() => {
    const views = ['front'];
    const getSideImage = (item) => (
      item?.captured_image
      || item?.capturedImage
      || item?.pipeline?.captured_image
      || item?.raw_image
      || item?.marked_image
      || null
    );
    const hasLeft = Boolean(
      sideCaptures?.left ||
      sideResults.some((item) => item?.key === 'left' && getSideImage(item))
    );
    const hasRight = Boolean(
      sideCaptures?.right ||
      sideResults.some((item) => item?.key === 'right' && getSideImage(item))
    );
    if (hasLeft) views.push('left');
    if (hasRight) views.push('right');
    return views;
  }, [sideCaptures, sideResults]);

  const hasMultiAnglePreview = availableConcernViews.length > 1;
  const isSelectedSkinConditionConcern = Boolean(
    selectedConcernName && isSkinConditionName(selectedConcernName),
  );
  const showConcernAngleNavigation = hasMultiAnglePreview && !isSelectedSkinConditionConcern;

  const moveConcernPreviewBy = useCallback((direction) => {
    if (!showConcernAngleNavigation) return;
    const idx = availableConcernViews.indexOf(selectedConcernView);
    const currentIndex = idx >= 0 ? idx : 0;
    const nextIndex = (currentIndex + direction + availableConcernViews.length) % availableConcernViews.length;
    setSelectedConcernView(availableConcernViews[nextIndex]);
  }, [availableConcernViews, showConcernAngleNavigation, selectedConcernView]);

  const drawConcernPreviewDetections = useCallback((ctx, conditionName, detectionsToUse, imgWidth) => {
    if (!ctx || !conditionName || !Array.isArray(detectionsToUse) || detectionsToUse.length === 0) return;
    const drawListRaw = getDetectionsToDraw(conditionName, detectionsToUse, imgWidth);

    const iou = (a, b) => {
      const bbA = a?.bbox || [];
      const bbB = b?.bbox || [];
      if (bbA.length !== 4 || bbB.length !== 4) return 0;
      const [ax1, ay1, ax2, ay2] = bbA;
      const [bx1, by1, bx2, by2] = bbB;
      const ix1 = Math.max(ax1, bx1);
      const iy1 = Math.max(ay1, by1);
      const ix2 = Math.min(ax2, bx2);
      const iy2 = Math.min(ay2, by2);
      if (ix2 <= ix1 || iy2 <= iy1) return 0;
      const inter = (ix2 - ix1) * (iy2 - iy1);
      const areaA = Math.max(1, (ax2 - ax1) * (ay2 - ay1));
      const areaB = Math.max(1, (bx2 - bx1) * (by2 - by1));
      return inter / (areaA + areaB - inter);
    };

    const clusters = [];
    const used = new Array(drawListRaw.length).fill(false);
    for (let i = 0; i < drawListRaw.length; i += 1) {
      if (used[i]) continue;
      const base = drawListRaw[i];
      const cluster = [base];
      used[i] = true;
      for (let j = i + 1; j < drawListRaw.length; j += 1) {
        if (used[j]) continue;
        if (iou(base, drawListRaw[j]) >= 0.75) {
          used[j] = true;
          cluster.push(drawListRaw[j]);
        }
      }
      clusters.push(cluster);
    }

    const drawList = clusters.map((list) => {
      const best = list.reduce((acc, d) => ((d?.confidence ?? 0) > (acc?.confidence ?? 0) ? d : acc), list[0]);
      return { det: best, stackedCount: list.length };
    });

    drawList.forEach(({ det, stackedCount }) => {
      if (det?.ui_hidden === true) return;
      const bbox = det?.bbox || [];
      if (bbox.length !== 4) return;
      const [x1, y1, x2, y2] = bbox;
      const classId = det.class_id;
      const color = getColorByClassId(classId);
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;
      const width = x2 - x1;
      const height = y2 - y1;
      const radius = Math.max(width, height) / 2;
      const suppressOutlines = false;

      ctx.beginPath();
      if (classId === 4 || classId === 6 || classId === 12 || classId === 14 || classId === 1 || classId === 2 || classId === 9 || classId === 5 || classId === 10) {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      } else if (classId === 0 || classId === 11) {
        const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
        const r = rgbMatch ? rgbMatch[1] : '0';
        const g = rgbMatch ? rgbMatch[2] : '191';
        const b = rgbMatch ? rgbMatch[3] : '255';
        const featheredRadius = radius * 1.2;
        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, featheredRadius);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.35)`);
        gradient.addColorStop(0.45, `rgba(${r},${g},${b},0.18)`);
        gradient.addColorStop(0.75, `rgba(${r},${g},${b},0.06)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
        const cornerRadius = Math.min(width, height) * 0.2;
        ctx.moveTo(x1 + cornerRadius, y1);
        ctx.lineTo(x2 - cornerRadius, y1);
        ctx.arcTo(x2, y1, x2, y1 + cornerRadius, cornerRadius);
        ctx.lineTo(x2, y2 - cornerRadius);
        ctx.arcTo(x2, y2, x2 - cornerRadius, y2, cornerRadius);
        ctx.lineTo(x1 + cornerRadius, y2);
        ctx.arcTo(x1, y2, x1, y2 - cornerRadius, cornerRadius);
        ctx.lineTo(x1, y1 + cornerRadius);
        ctx.arcTo(x1, y1, x1 + cornerRadius, y1, cornerRadius);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
      } else if (classId === 3 || classId === 15) {
        const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
        const r = rgbMatch ? rgbMatch[1] : '200';
        const g = rgbMatch ? rgbMatch[2] : '0';
        const b = rgbMatch ? rgbMatch[3] : '255';
        const offsetUp = radius * 0.22;
        const cy = centerY - offsetUp;
        const featheredRadius = radius * 1.25;
        const gradient = ctx.createRadialGradient(centerX, cy, 0, centerX, cy, featheredRadius);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.65)`);
        gradient.addColorStop(0.3, `rgba(${r},${g},${b},0.48)`);
        gradient.addColorStop(0.55, `rgba(${r},${g},${b},0.32)`);
        gradient.addColorStop(0.78, `rgba(${r},${g},${b},0.2)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0.18)`);
        const curveBulge = radius * 0.32;
        ctx.beginPath();
        ctx.arc(centerX, cy, radius, 0, Math.PI);
        ctx.quadraticCurveTo(centerX, cy + curveBulge, centerX + radius, cy);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(centerX, cy, radius, 0, Math.PI);
        ctx.quadraticCurveTo(centerX, cy + curveBulge, centerX + radius, cy);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.lineWidth = 2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke();
      } else {
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }

      if (!suppressOutlines && stackedCount && stackedCount > 1) {
        const extra = Math.min(3, stackedCount - 1);
        for (let k = 1; k <= extra; k += 1) {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.8;
          ctx.setLineDash([4, 3]);
          if (classId === 0 || classId === 11) {
            const pad = 3 * k;
            ctx.strokeRect(x1 - pad, y1 - pad, Math.max(1, (x2 - x1) + pad * 2), Math.max(1, (y2 - y1) + pad * 2));
          } else {
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius + (3 * k), 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      if (!suppressOutlines && det?.low_confidence) {
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x1, y1, Math.max(1, x2 - x1), Math.max(1, y2 - y1));
        ctx.restore();
      }
    });
  }, []);

  const getFaceSideFilterContext = useCallback((sideResult, imageWidth) => {
    const fallbackMid = Number(imageWidth) > 0 ? imageWidth / 2 : null;
    const roiPayload = sideResult?.roi_region_landmarks_px || {};
    const faceRegion = roiPayload?.face;
    const anchors = Array.isArray(faceRegion?.anchor_landmarks_px) ? faceRegion.anchor_landmarks_px : [];

    const leftCheek = anchors.find((pt) => Number(pt?.i) === 234 && Number.isFinite(Number(pt?.x)));
    const rightCheek = anchors.find((pt) => Number(pt?.i) === 454 && Number.isFinite(Number(pt?.x)));
    const isMirrored = Boolean(leftCheek && rightCheek && Number(leftCheek.x) > Number(rightCheek.x));

    // MediaPipe landmark #1 is nose tip; it's the most reliable face midline anchor.
    const noseTip = anchors.find((pt) => Number(pt?.i) === 1 && Number.isFinite(Number(pt?.x)));
    const getRegionCenterX = (regionName) => {
      const region = roiPayload?.[regionName];
      const roiBbox = region?.roi_bbox;
      if (roiBbox && Number.isFinite(Number(roiBbox.x1)) && Number.isFinite(Number(roiBbox.x2))) {
        return (Number(roiBbox.x1) + Number(roiBbox.x2)) / 2;
      }
      const regionAnchors = Array.isArray(region?.anchor_landmarks_px) ? region.anchor_landmarks_px : [];
      const xs = regionAnchors.map((pt) => Number(pt?.x)).filter((x) => Number.isFinite(x));
      if (xs.length > 0) return xs.reduce((sum, x) => sum + x, 0) / xs.length;
      return null;
    };

    let midX = fallbackMid;
    if (noseTip) {
      midX = Number(noseTip.x);
    } else {
      const anchorXs = anchors
        .map((pt) => Number(pt?.x))
        .filter((x) => Number.isFinite(x));
      if (anchorXs.length > 0) {
        const minX = Math.min(...anchorXs);
        const maxX = Math.max(...anchorXs);
        midX = (minX + maxX) / 2;
      } else {
        const bbox = faceRegion?.roi_bbox;
        if (bbox && Number.isFinite(Number(bbox.x1)) && Number.isFinite(Number(bbox.x2))) {
          midX = (Number(bbox.x1) + Number(bbox.x2)) / 2;
        }
      }
    }

    const leftCheekX = getRegionCenterX('left_cheek');
    const rightCheekX = getRegionCenterX('right_cheek');

    let leftCaptureHalf = isMirrored ? 'right' : 'left';
    let rightCaptureHalf = isMirrored ? 'left' : 'right';

    if (Number.isFinite(midX) && Number.isFinite(leftCheekX) && Number.isFinite(rightCheekX)) {
      leftCaptureHalf = leftCheekX >= midX ? 'right' : 'left';
      rightCaptureHalf = rightCheekX >= midX ? 'right' : 'left';
      if (leftCaptureHalf === rightCaptureHalf) {
        rightCaptureHalf = leftCaptureHalf === 'right' ? 'left' : 'right';
      }
    } else if (Number.isFinite(midX) && Number.isFinite(rightCheekX)) {
      rightCaptureHalf = rightCheekX >= midX ? 'right' : 'left';
      leftCaptureHalf = rightCaptureHalf === 'right' ? 'left' : 'right';
    } else if (Number.isFinite(midX) && Number.isFinite(leftCheekX)) {
      leftCaptureHalf = leftCheekX >= midX ? 'right' : 'left';
      rightCaptureHalf = leftCaptureHalf === 'right' ? 'left' : 'right';
    }

    return {
      midX,
      leftCaptureHalf,
      rightCaptureHalf,
    };
  }, []);

  const filterDetectionsForSelectedView = useCallback((detections, view, faceFilterContext, imageWidth) => {
    if (!Array.isArray(detections) || detections.length === 0) return [];
    if (view === 'front') return detections;

    const midX = Number.isFinite(faceFilterContext?.midX)
      ? faceFilterContext.midX
      : (Number(imageWidth) > 0 ? imageWidth / 2 : null);
    if (!Number.isFinite(midX)) return detections;

    const targetHalf = view === 'left'
      ? (faceFilterContext?.rightCaptureHalf || 'right')
      : (faceFilterContext?.leftCaptureHalf || 'left');

    return detections.filter((det) => {
      const bbox = Array.isArray(det?.bbox) ? det.bbox : [];
      if (bbox.length !== 4) return true;
      const [x1, , x2] = bbox;
      const centerX = (Number(x1) + Number(x2)) / 2;
      if (!Number.isFinite(centerX)) return true;
      return targetHalf === 'right' ? centerX >= midX : centerX <= midX;
    });
  }, []);

  const renderSelectedConcernPreview = useCallback((conditionName, view = selectedConcernView) => {
    const sideResult = sideResults.find((item) => item?.key === view) || null;
    const sideImageFromResult = sideResult?.raw_image
      || sideResult?.captured_image
      || sideResult?.capturedImage
      || sideResult?.pipeline?.captured_image
      || null;
    const sideRawImage = view === 'left'
      ? (sideCaptures?.left || sideImageFromResult)
      : view === 'right'
        ? (sideCaptures?.right || sideImageFromResult)
        : null;
    const sideFallbackImage = sideRawImage || sideResult?.marked_image || null;
    const baseImage = view === 'front' ? previewBaseImage : (sideRawImage || null);

    if (!conditionName) {
      if (view === 'front') {
        setSelectedConcernPreviewImage(skinResultsFromState?.markedImage || previewBaseImage || null);
      } else {
        setSelectedConcernPreviewImage(sideFallbackImage);
      }
      return;
    }

    const skinConditions = skinResultsFromState?.skinConditions || {};
    if (view === 'front' && isSkinConditionName(conditionName)) {
      const canonical = canonicalSkinConditionName(conditionName) || conditionName;
      const conditionKey = conditionName.toLowerCase().replace(/\s+/g, '_');
      let maskUrl = skinConditions?.[conditionKey]?.mask || null;
      if (typeof maskUrl === 'string' && maskUrl.startsWith('/') && API_BASE_URL) {
        maskUrl = `${API_BASE_URL.replace(/\/$/, '')}${maskUrl}`;
      }
      if (maskUrl) {
        setSelectedConcernPreviewImage(maskUrl);
        return;
      }
      if (sessionId) {
        fetchSkinConcernMask({
          sessionId,
          displayName: canonical,
          appSessionToken: token || null,
        }).then((fetchedMask) => {
          if (fetchedMask) {
            setSelectedConcernPreviewImage(fetchedMask);
          }
        }).catch(() => {});
      }
    }

    if (!baseImage) {
      setSelectedConcernPreviewImage(view === 'front' ? (skinResultsFromState?.markedImage || null) : null);
      return;
    }

    const sideDetections = Array.isArray(sideResult?.detections)
      ? sideResult.detections
      : (Array.isArray(sideResult?.pipeline?.detections) ? sideResult.pipeline.detections : []);

    const sourceDetections = view === 'front'
      ? (skinResultsFromState?.frontDetections || skinResultsFromState?.detections || [])
      : sideDetections;

    const filteredDetections = sourceDetections.filter((det) => {
      const className = normalizeConditionName(det?.class_name || det?.className || 'Unknown');
      return className === conditionName;
    });

    if (!filteredDetections.length) {
      setSelectedConcernPreviewImage(baseImage);
      return;
    }

    const canvas = concernPreviewCanvasRef.current;
    if (!canvas) {
      setSelectedConcernPreviewImage(baseImage);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const faceFilterContext = getFaceSideFilterContext(sideResult, img.width);
        const detectionsForView = filterDetectionsForSelectedView(filteredDetections, view, faceFilterContext, img.width);

        if (!detectionsForView.length) {
          setSelectedConcernPreviewImage(baseImage);
          return;
        }

        drawConcernPreviewDetections(ctx, conditionName, detectionsForView, img.width);
        setSelectedConcernPreviewImage(canvas.toDataURL('image/jpeg', 0.95));
      } catch {
        setSelectedConcernPreviewImage(baseImage);
      }
    };
    img.onerror = () => {
      setSelectedConcernPreviewImage(baseImage);
    };
    img.src = baseImage;
  }, [
    API_BASE_URL,
    drawConcernPreviewDetections,
    filterDetectionsForSelectedView,
    getFaceSideFilterContext,
    previewBaseImage,
    selectedConcernView,
    sessionId,
    sideCaptures,
    sideResults,
    skinResultsFromState,
    token,
  ]);

  const selectedConcernSeverityGradientStyle = useMemo(
    () => buildSeverityBarGradientStyleVars(selectedConcernName),
    [selectedConcernName],
  );

  const selectedSeverityBarLabels = useMemo(
    () => getSeverityBarLabels(selectedConcernName),
    [selectedConcernName],
  );

  useEffect(() => {
    if (!detailedConcernItems.length) {
      setSelectedConcernName(null);
      setIsConcernTextExpanded(false);
      return;
    }
    const hasCurrentSelection = detailedConcernItems.some((item) => item.class_name === selectedConcernName);
    if (!hasCurrentSelection) {
      setSelectedConcernName(detailedConcernItems[0].class_name);
      setIsConcernTextExpanded(false);
    }
  }, [detailedConcernItems, selectedConcernName]);

  useEffect(() => {
    if (!sessionId || sideCaptures?.left || sideCaptures?.right) return;
    let cancelled = false;
    void getSideCaptures(sessionId).then((stored) => {
      if (cancelled || !stored) return;
      setRestoredSideCaptures((prev) => prev || stored);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, sideCaptures]);

  useEffect(() => {
    if (!availableConcernViews.length) return;
    if (!availableConcernViews.includes(selectedConcernView)) {
      setSelectedConcernView(availableConcernViews[0]);
    }
  }, [availableConcernViews, selectedConcernView]);

  useEffect(() => {
    if (!isSelectedSkinConditionConcern) return;
    if (selectedConcernView !== 'front') {
      setSelectedConcernView('front');
    }
  }, [isSelectedSkinConditionConcern, selectedConcernView]);

  useEffect(() => {
    renderSelectedConcernPreview(selectedConcernName, selectedConcernView);
  }, [renderSelectedConcernPreview, selectedConcernName, selectedConcernView]);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, {
      ...current,
      sessionId,
      selectedCondition: selectedConcernName || null,
      imageView: selectedConcernView,
    });
  }, [sessionId, selectedConcernName, selectedConcernView]);

  return (
    <div
      className="skin-routine-page-shell page-bg"
      id="skin-routine-page-shell"
      ref={scrollShellRef}
      style={{ '--routine-card-bg-image': `url(${process.env.PUBLIC_URL}/BG/BG.png)` }}
    >
      {/* Navigation Buttons */}
      <div className="skin-routine-page-nav" id="skin-routine-page-nav">
        <button
          type="button"
          className="skin-routine-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="skin-routine-page-title-wrapper">
          <SalonHeaderActions homeButtonClassName="skin-routine-page-home-button" onHome={handleHome} />
        </div>
      </div>

      <main className="skin-routine-page-main" id="skin-routine-page-main" ref={scrollMainRef}>
        <div className="skin-routine-page-container" id="skin-routine-page-container">
          <section className="skin-routine-page-layout" id="skin-routine-page-layout">

            {/* Section navigation bar */}
            <nav className="skin-routine-section-nav" aria-label="Page sections">
              {NAV_SECTIONS.map((sec) => (
                <button
                  key={sec.id}
                  className={`skin-routine-section-nav-btn${activeNavSection === sec.id ? ' active' : ''}`}
                  onClick={() => scrollToSection(sec.id)}
                  type="button"
                >
                  {sec.label}
                </button>
              ))}
            </nav>

            {/* User / Session overview card */}
            <div className="skin-routine-user-card" id="section-user-info">
            <span className="skin-routine-profile-date">{analysisDateText}</span>
            <div className="skin-routine-user-info">
              {userImage && (
                <div className="skin-routine-user-image">
                  <img src={userImage} alt="Your skin analysis" />
                </div>
              )}
              <div className="skin-routine-user-details">
                <h2 className="skin-routine-user-name">{userName}</h2>
                <p className="skin-routine-user-subtitle">{profileSubtitle}</p>
              </div>
              {overallSkinScore != null ? (
                <div className="skin-routine-score-tile skin-routine-score-tile-inline">
                  <div className="skin-routine-score-canvas-wrapper" role="img" aria-label={`Overall skin score ${overallSkinScore} out of 100`}>
                    <canvas ref={skinGaugeCanvasRef} className="skin-routine-score-canvas" width="400" height="320" />
                    <div className="skin-routine-score-display">
                      <div className="skin-routine-score-display-value">{overallSkinScore}</div>
                      <div className="skin-routine-score-display-label">Skin Score</div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="skin-routine-report-strip">
              <div className="skin-routine-env-tile">
                <p className="routine-tile-title">Environmental Context</p>
                {aqiData && aqiData.success ? (
                  <div className="skin-routine-env-grid">
                    {aqiData.location ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Location</span>
                        <span className="skin-routine-env-value">{aqiData.location}</span>
                      </span>
                    ) : null}
                    {aqiData.temperature != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V5a2 2 0 1 0-4 0v9.76a4 4 0 1 0 4 0z"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Temp</span>
                        <span className="skin-routine-env-value">{aqiData.temperature} C</span>
                      </span>
                    ) : null}
                    {(aqiData.uv_level || aqiData.uv_index != null) ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">UV Level</span>
                        <span className="skin-routine-env-value" style={{ color: getUvIndexColor(aqiData.uv_index ?? 0) }}>
                          {aqiData.uv_level || getUvDescription(aqiData.uv_index)}
                        </span>
                      </span>
                    ) : null}
                    {aqiData.humidity != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3s5 5.2 5 9a5 5 0 0 1-10 0c0-3.8 5-9 5-9z"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Humidity</span>
                        <span className="skin-routine-env-value">{aqiData.humidity}%</span>
                      </span>
                    ) : null}
                    {aqiData.wind_speed != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h12a2 2 0 1 0-2-2"/><path d="M3 18h14a2.5 2.5 0 1 1-2.5 2.5"/><path d="M3 6h9a2 2 0 1 1-2-2"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Wind</span>
                        <span className="skin-routine-env-value">{aqiData.wind_speed} km/h</span>
                      </span>
                    ) : null}
                    {aqiData.aqi != null ? (
                      <span className="skin-routine-env-item">
                        <span className="skin-routine-env-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h6l3-8 3 16 3-8h3"/></svg>
                        </span>
                        <span className="skin-routine-env-label skin-text-small">Air Quality</span>
                        <span className="skin-routine-env-value">{aqiData.aqi}</span>
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <p className="skin-routine-score-unavailable">Environment data unavailable</p>
                )}
              </div>
            </div>
            </div>

            {/* Progressive display: 1) Concerns, 2) Summary, 3) Routine, 4) Next: treatments */}
            {!skinResultsFromState ? (
              <div className="skin-routine-error">
                <p>Missing required data. Please go back and complete the skin analysis.</p>
                <button onClick={handleBack}>Go back</button>
              </div>
            ) : (
              <div className="skin-routine-content-wrapper skin-routine-content-no-tabs skin-routine-sections">
                {/* 1. Concerns first: show immediately */}
                {(concernsBySeverity.allItems.length > 0 || skinResultsFromState?.skinScore || skinResultsFromState?.detections || skinResultsFromState?.skinConditions) && (
                  <div className="skin-routine-section-block skin-routine-concerns-block" id="section-concerns">
                    <h2 className="skin-routine-section-heading">
                      <span className="skin-routine-section-heading-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      </span>
                      Skin Concerns & Analysis
                    </h2>
                    {concernsBySeverity.allItems.length > 0 && radarModel ? (
                        <div className="skin-routine-detailed-concerns-block">
                          <h3 className="skin-routine-concerns-subheading">Detailed Skin Concerns</h3>
                          {selectedConcernItem ? (
                            <div className="skin-routine-concern-preview-panel">
                              <div className="skin-routine-concern-preview-copy">
                                <div className="skin-routine-concern-preview-header skin-routine-concern-preview-header--copy">
                                  <div>
                                    <h4 className="skin-routine-concern-preview-title">{selectedConcernItem.label}</h4>
                                    <div className="skin-routine-concern-preview-score-wrap skin-routine-concern-preview-score-wrap--copy" role="img" aria-label={`${selectedConcernItem.score} percent concern score`}>
                                      <div className="skin-routine-concern-gauge-wrapper">
                                        <canvas ref={concernGaugeCopyCanvasRef} className="skin-routine-concern-gauge-canvas" width="400" height="280" />
                                        <div className="skin-routine-concern-gauge-display">
                                          <div className="skin-routine-concern-gauge-value">{selectedConcernItem.score}</div>
                                          <div className="skin-routine-concern-gauge-label">Concern Score</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <span className={`skin-routine-detailed-severity skin-routine-severity-${getScoreOutlineColor(selectedConcernItem.score)}`}>
                                    {selectedConcernItem.score <= 39 ? 'Critical' :
                                      selectedConcernItem.score <= 59 ? 'High' :
                                      selectedConcernItem.score <= 74 ? 'Moderate' :
                                      selectedConcernItem.score <= 89 ? 'Mild' : 'Healthy'}
                                  </span>
                                </div>
                                <p className="skin-routine-concern-preview-text skin-routine-concern-preview-text--copy">
                                  {selectedConcernItem ? (
                                    <>
                                      <strong>{selectedConcernItem.label}:</strong> {selectedConcernDescription || 'Ollama deep-dive is being generated for this concern.'}
                                    </>
                                  ) : (
                                    'Select any concern below to inspect its focused preview and severity in the same flow as the results page.'
                                  )}
                                </p>
                              </div>
                              <div className="skin-routine-concern-preview-media">
                                <div className="skin-routine-concern-preview-frame">
                                  {showConcernAngleNavigation ? (
                                    <>
                                      <div className="skin-routine-preview-arrow-wrap skin-routine-preview-arrow-wrap--left">
                                        <button
                                          type="button"
                                          className="skin-routine-preview-arrow"
                                          onClick={() => moveConcernPreviewBy(-1)}
                                          aria-label="View previous angle"
                                        >
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="15 18 9 12 15 6" />
                                          </svg>
                                        </button>
                                        <span className="skin-routine-preview-arrow-label">Prev</span>
                                      </div>
                                      <div className="skin-routine-preview-angle-chip">
                                        {selectedConcernView.charAt(0).toUpperCase() + selectedConcernView.slice(1)}
                                      </div>
                                      <div className="skin-routine-preview-arrow-wrap skin-routine-preview-arrow-wrap--right">
                                        <button
                                          type="button"
                                          className="skin-routine-preview-arrow"
                                          onClick={() => moveConcernPreviewBy(1)}
                                          aria-label="View next angle"
                                        >
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="9 18 15 12 9 6" />
                                          </svg>
                                        </button>
                                        <span className="skin-routine-preview-arrow-label">Next</span>
                                      </div>
                                    </>
                                  ) : null}
                                  {selectedConcernPreviewImage ? (
                                    <img
                                      src={selectedConcernPreviewImage}
                                      alt={`${selectedConcernItem.label} preview ${selectedConcernView}`}
                                      className="skin-routine-concern-preview-image"
                                    />
                                  ) : (
                                    <div className="skin-routine-concern-preview-empty">Preview unavailable</div>
                                  )}
                                  <canvas ref={concernPreviewCanvasRef} className="skin-routine-concern-preview-canvas" aria-hidden="true" />
                                  {selectedConcernSeverityGradientStyle ? (
                                    <div
                                      key={selectedConcernName || 'severity'}
                                      className="severity-bar-gradient-only skin-routine-concern-preview-severity"
                                      style={selectedConcernSeverityGradientStyle}
                                    >
                                      <div
                                        className="severity-bar-gradient"
                                        style={{ background: selectedConcernSeverityGradientStyle['--gradient-bg'] }}
                                      />
                                      <div className="severity-bar-labels">
                                        {selectedSeverityBarLabels.triple ? (
                                          <>
                                            <span>{selectedSeverityBarLabels.left}</span>
                                            <span>{selectedSeverityBarLabels.middle}</span>
                                            <span>{selectedSeverityBarLabels.right}</span>
                                          </>
                                        ) : (
                                          <>
                                            <span>{selectedSeverityBarLabels.left}</span>
                                            <span>{selectedSeverityBarLabels.right}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}

                                  <div className="skin-routine-concern-preview-score-wrap skin-routine-concern-preview-score-wrap--overlay" role="img" aria-label={`${selectedConcernItem.score} percent concern score`}>
                                    <div className="skin-routine-concern-preview-score-head">
                                      <h4 className="skin-routine-concern-preview-title">{selectedConcernItem.label}</h4>
                                      <span className={`skin-routine-detailed-severity skin-routine-severity-${getScoreOutlineColor(selectedConcernItem.score)}`}>
                                        {selectedConcernItem.score <= 39 ? 'Critical' :
                                          selectedConcernItem.score <= 59 ? 'High' :
                                          selectedConcernItem.score <= 74 ? 'Moderate' :
                                          selectedConcernItem.score <= 89 ? 'Mild' : 'Healthy'}
                                      </span>
                                    </div>
                                    <div className="skin-routine-concern-mobile-compact-score" aria-hidden="true">
                                      <span className="skin-routine-concern-mobile-compact-name">{selectedConcernItem.label}</span>
                                      <span className="skin-routine-concern-mobile-compact-value">{selectedConcernItem.score}%</span>
                                    </div>
                                    <div className="skin-routine-concern-preview-score-row">
                                      <div className="skin-routine-concern-gauge-wrapper">
                                        <canvas ref={concernGaugeOverlayCanvasRef} className="skin-routine-concern-gauge-canvas" width="400" height="280" />
                                        <div className="skin-routine-concern-gauge-display">
                                          <div className="skin-routine-concern-gauge-value">{selectedConcernItem.score}</div>
                                          <div className="skin-routine-concern-gauge-label">Concern Score</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <p
                                  className="skin-routine-concern-preview-text skin-routine-concern-preview-text--below-image"
                                  onClick={handleToggleConcernTextExpand}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleToggleConcernTextExpand();
                                    }
                                  }}
                                  data-expanded={isConcernTextExpanded}
                                  aria-expanded={isConcernTextExpanded}
                                >
                                  {selectedConcernItem ? (
                                    <>
                                      <strong>{selectedConcernItem.label}:</strong> {selectedConcernDescription || 'Ollama deep-dive is being generated for this concern.'}
                                    </>
                                  ) : (
                                    'Select any concern below to inspect its focused preview and severity in the same flow as the results page.'
                                  )}
                                </p>
                              </div>
                            </div>
                          ) : null}
                          <div className="skin-routine-detailed-concern-grid">
                            {detailedConcernItems.map((item, idx) => {
                              const score = Math.max(0, Math.min(100, Number(item.score || 0)));
                              const scoreCategory = getScoreOutlineColor(score);
                              const severityLabel =
                                scoreCategory === 'critical' ? 'Critical' :
                                scoreCategory === 'poor' ? 'High' :
                                scoreCategory === 'fair' ? 'Moderate' :
                                scoreCategory === 'good' ? 'Mild' : 'Healthy';
                              return (
                                <div
                                  key={`detail-${item.class_name}-${idx}`}
                                  className={`skin-routine-detailed-card score-outline-${scoreCategory}${selectedConcernName === item.class_name ? ' is-selected' : ''}${selectedConcernName === item.class_name && animatedConcernName === item.class_name ? ' is-selected-animate' : ''}`}
                                  style={{ '--selected-accent': item.color || '#38bdf8' }}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleSelectConcern(item.class_name)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleSelectConcern(item.class_name);
                                    }
                                  }}
                                >
                                  <div className="skin-routine-detailed-card-top">
                                    <span className="skin-routine-detailed-name">{item.label}</span>
                                    <span className={`skin-routine-detailed-severity skin-routine-severity-${scoreCategory}`}>{severityLabel}</span>
                                  </div>
                                  <div className="skin-routine-detailed-bar-wrap">
                                    <div
                                      className="skin-routine-detailed-bar-fill"
                                      style={{ width: `${score}%`, background: item.color || '#0f766e' }}
                                    />
                                  </div>
                                  <div className="skin-routine-detailed-score-row">
                                    <span className="skin-routine-detailed-score-num" style={{ color: item.color || '#0f766e' }}>{score}%</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                    ) : (
                      <div className="no-conditions-message">
                        <p>No significant skin conditions detected.</p>
                        <p className="no-conditions-subtitle">Your skin looks healthy!</p>
                      </div>
                    )}
                  </div>
                )}

                {suggestConfigLoaded && showAdvancedCharts && concernsBySeverity.allItems.length > 0 && radarModel && (
                  <div className="skin-routine-section-block skin-routine-radar-analysis-block" id="section-radar">
                    <h2 className="skin-routine-section-heading">
                      <span className="skin-routine-section-heading-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                      </span>
                      Skin Analysis Report & Deep Dive
                    </h2>
                    <div className="skin-routine-section-content">
                      {hasBeforeAfterRadar && (
                        <div className="skin-routine-radar-tabs" role="tablist" aria-label="Skin analysis report comparison tabs">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={radarComparisonTab === 'before'}
                            className={`skin-routine-radar-tab ${radarComparisonTab === 'before' ? 'active' : ''}`}
                            onClick={() => setRadarComparisonTab('before')}
                          >
                            Before
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={radarComparisonTab === 'after'}
                            className={`skin-routine-radar-tab ${radarComparisonTab === 'after' ? 'active' : ''}`}
                            onClick={() => setRadarComparisonTab('after')}
                          >
                            After
                          </button>
                        </div>
                      )}

                      <div className="skin-routine-concerns-overview-grid">
                        <div className="skin-routine-radar-panel">
                          <div className="skin-routine-radar-panel-header">
                            <h3 className="skin-routine-concerns-subheading">
                              {hasBeforeAfterRadar ? (radarComparisonTab === 'before' ? 'Before Skin Analysis Report' : 'After Skin Analysis Report') : 'Skin Analysis Report'}
                            </h3>
                            <button
                              type="button"
                              className="skin-routine-radar-enlarge-btn"
                              onClick={() => setIsRadarEnlargedOpen(true)}
                              aria-label="Enlarge radar chart to fullscreen view"
                              title="Expand chart"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                              </svg>
                            </button>
                          </div>
                          <div className="skin-routine-radar-meta">
                            {isAfterComparisonView ? (
                              <>
                                <span className="skin-routine-radar-meta-score">
                                  Score: {overallSkinScore != null ? `${overallSkinScore}/100` : '--'}
                                </span>
                                <span className="skin-routine-radar-meta-date">
                                  From {previousAnalysisDateText || '--'} to {analysisDateText || '--'}
                                  {radarDaysBetween != null ? ` (${radarDaysBetween} day${radarDaysBetween === 1 ? '' : 's'})` : ''}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="skin-routine-radar-meta-score">
                                  Score: {activeRadarScore != null ? `${activeRadarScore}/100` : '--'}
                                </span>
                                <span className="skin-routine-radar-meta-date">
                                  {hasBeforeAfterRadar && radarComparisonTab === 'before' ? 'Before Date: ' : 'Date: '}
                                  {activeRadarDate || '--'}
                                </span>
                              </>
                            )}
                          </div>
                          <svg className="skin-routine-radar-svg" viewBox={`0 0 ${activeRadarModel.size} ${activeRadarModel.size}`} role="img" aria-label="Skin analysis report chart">
                            {activeRadarModel.levels.map((level) => {
                              const r = (level / 100) * activeRadarModel.radius;
                              return (
                                <circle
                                  key={`level-${level}`}
                                  cx={activeRadarModel.cx}
                                  cy={activeRadarModel.cy}
                                  r={r}
                                  fill="none"
                                  stroke="rgba(100, 116, 139, 0.22)"
                                  strokeWidth="1"
                                />
                              );
                            })}
                            {activeRadarModel.axis.map((item, idx) => (
                              <line
                                key={`axis-${idx}`}
                                x1={activeRadarModel.cx}
                                y1={activeRadarModel.cy}
                                x2={activeRadarModel.cx + Math.cos(item.angle) * activeRadarModel.radius}
                                y2={activeRadarModel.cy + Math.sin(item.angle) * activeRadarModel.radius}
                                stroke="rgba(100, 116, 139, 0.2)"
                                strokeWidth="1"
                              />
                            ))}
                            {isAfterComparisonView ? (
                              <>
                                <polygon
                                  points={activeRadarModel.beforePolygon}
                                  fill="none"
                                  stroke="rgba(249, 115, 22, 0.95)"
                                  strokeWidth="1.8"
                                  strokeDasharray="5 4"
                                />
                                <polygon
                                  points={activeRadarModel.afterPolygon}
                                  fill="none"
                                  stroke="rgba(37, 99, 235, 0.95)"
                                  strokeWidth="2.2"
                                />
                                {activeRadarModel.axis.map((item, idx) => (
                                  <g
                                    key={`compare-point-${idx}`}
                                    className="skin-routine-radar-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openRadarConcernModal(idx)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRadarConcernModal(idx);
                                      }
                                    }}
                                  >
                                    {item.hasBefore && item.hasAfter ? (
                                      <line
                                        x1={item.beforePointX}
                                        y1={item.beforePointY}
                                        x2={item.afterPointX}
                                        y2={item.afterPointY}
                                        stroke={
                                          item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.95)'
                                            : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.95)'
                                              : 'rgba(148, 163, 184, 0.9)'
                                        }
                                        strokeWidth="2"
                                        strokeDasharray={item.changeKind === 'stable' ? '3 3' : 'none'}
                                      />
                                    ) : null}
                                    {item.hasBefore ? <circle cx={item.beforePointX} cy={item.beforePointY} r="2.8" fill="rgba(249, 115, 22, 0.95)" /> : null}
                                    {item.hasAfter ? (
                                      <circle
                                        cx={item.afterPointX}
                                        cy={item.afterPointY}
                                        r="3.8"
                                        fill={
                                          item.changeKind === 'new' ? 'rgba(168, 85, 247, 0.98)'
                                            : item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.98)'
                                              : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.98)'
                                                : 'rgba(37, 99, 235, 0.95)'
                                        }
                                      />
                                    ) : null}
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className={`skin-routine-radar-label skin-routine-radar-label--${item.changeKind}`}
                                    >
                                      {item.label}
                                    </text>
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY + 13}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className={`skin-routine-radar-score skin-routine-radar-score--${item.changeKind}`}
                                    >
                                      {item.changeKind === 'new'
                                        ? `A:${item.afterScore != null ? `${item.afterScore}%` : '--'} New`
                                        : item.changeKind === 'missing'
                                          ? `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} Missing`
                                          : `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} A:${item.afterScore != null ? `${item.afterScore}%` : '--'} ${item.delta != null ? `${item.delta > 0 ? '+' : ''}${item.delta}` : ''}`}
                                    </text>
                                  </g>
                                ))}
                              </>
                            ) : (
                              <>
                                <polygon
                                  points={activeRadarModel.polygon}
                                  fill="none"
                                  stroke="rgba(37, 99, 235, 0.95)"
                                  strokeWidth="2"
                                />
                                {activeRadarModel.axis.map((item, idx) => (
                                  <g
                                    key={`point-${idx}`}
                                    className="skin-routine-radar-clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openRadarConcernModal(idx)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRadarConcernModal(idx);
                                      }
                                    }}
                                  >
                                    <circle cx={item.pointX} cy={item.pointY} r="3.6" fill="rgba(37, 99, 235, 0.95)" />
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className="skin-routine-radar-label"
                                    >
                                      {item.label}
                                    </text>
                                    <text
                                      x={item.labelX + (item.labelOffsetX || 0)}
                                      y={item.labelY + 13}
                                      textAnchor={item.labelAnchor || 'middle'}
                                      className="skin-routine-radar-score"
                                    >
                                      {item.score}%
                                    </text>
                                  </g>
                                ))}
                              </>
                            )}
                          </svg>
                          <div className="skin-routine-radar-legend" aria-label="Radar chart color legend">
                            {isAfterComparisonView ? (
                              <>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before line</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After line</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Needs attention</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--new" />New in after</span>
                                <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--stable" />Stable</span>
                              </>
                            ) : (
                              <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />Current scan line</span>
                            )}
                          </div>
                        </div>

                        <div className="skin-routine-analysis-panel">
                          <h3 className="skin-routine-concerns-subheading">Analysis Deep Dive</h3>
                          {!hasCompletedProductsStep ? (
                            <p className="skin-routine-analysis-empty">Deep dive will start after recommendations are ready...</p>
                          ) : isLoadingConditionDescriptions ? (
                            <div className="skin-routine-summary-loading">
                              <div className="loading-spinner"></div>
                              <p className="skin-routine-summary-loading-text">Generating detailed analysis...</p>
                            </div>
                          ) : deepDiveLines.length > 0 ? (
                            <ul className="skin-routine-analysis-lines">
                              {deepDiveLines.map((line, idx) => (
                                <li key={`deep-line-${idx}`}>{line}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="skin-routine-analysis-empty">No high or moderate concerns require deep-dive analysis right now.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. AI Summary */}
                {showSummaryAndInsights && hasCompletedDeepDiveStep && (hasCompletedSummaryStep || summaryLoading || summary?.trim() || showFallbackSummary) && (
                  <div className="skin-routine-section-block skin-routine-ai-summary-block" id="section-ai-summary">
                    <h2 className="skin-routine-section-heading">
                      <span className="skin-routine-section-heading-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </span>
                      AI Skin Summary
                    </h2>
                    <div className="skin-routine-section-content">
                      <div className="skin-routine-summary-box">
                        <div className="skin-routine-summary-ai-header">
                          <span className="skin-routine-summary-ai-badge">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg>
                            AI Analysis
                          </span>
                          <span className="skin-routine-summary-ai-date">{analysisDateText}</span>
                        </div>

                        {isAiContentLoading ? (
                          <div className="skin-routine-summary-loading">
                            <div className="loading-spinner"></div>
                            <p className="skin-routine-summary-loading-text">Generating AI summary...</p>
                          </div>
                        ) : error ? (
                          <div className="skin-routine-summary-error-wrap">
                            <p className="skin-routine-summary-text skin-routine-summary-error">{error}</p>
                            <button type="button" className="skin-action-button skin-action-button-secondary" onClick={handleRetryRecommendations}>Retry recommendations</button>
                          </div>
                        ) : showFallbackSummary ? (
                          <p className="skin-routine-summary-text">{effectiveSummaryText}</p>
                        ) : summary ? (
                          <div
                            className="skin-routine-summary-text"
                            dangerouslySetInnerHTML={{ __html: formatAiSummaryHtml(effectiveSummaryText) }}
                          />
                        ) : (
                          <p className="skin-routine-summary-text">{effectiveSummaryText}</p>
                        )}
                      </div>
                      {isRoutineBackgroundRunning ? (
                        <p className="skin-routine-summary-loading-text">Building AI routine in background...</p>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* 3. Recommended Treatments (inline on this page) */}
                {suggestConfigLoaded && (
                  <div className="skin-routine-section-block skin-routine-treatments-block" id="section-treatments">
                    <h2 className="skin-routine-section-heading">
                      <span className="skin-routine-section-heading-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      </span>
                      Personalized Treatments
                    </h2>
                    <div className="skin-routine-section-content">
                      {isTreatmentsLoading ? (
                        <div className="skin-routine-summary-loading">
                          <div className="loading-spinner"></div>
                          <p className="skin-routine-summary-loading-text">Generating recommended treatments...</p>
                        </div>
                      ) : recommendedTreatments.length > 0 ? (
                        <div className="skin-routine-treatments-grid">
                          {recommendedTreatments.map((item, idx) => {
                            const title = item?.treatment || item?.name || `Treatment ${idx + 1}`;
                            const cartKey = treatmentRowCartKey(item, idx);
                            const cartQuantity = treatmentCartItems[cartKey]?.quantity || 0;
                            const imageUrl = resolveTreatmentImageUrl(item?.image_url);
                            const recommendationLine = getRecommendationLine(item)
                              .replace(/\s*ΓÇö\s*aligns with \d+ of your selected goal\(s\)/i, '')
                              .replace(/^aligns with \d+ selected goal\(s\)$/i, '')
                              .trim();
                            const aboutText = item?.about_treatment || item?.description || '';
                            const majorUspPlain = item?.major_usp ? stripHtml(item.major_usp) : '';
                            const sessionsText = item?.sessions || item?.session || item?.no_of_sittings || item?.sittings || item?.session_count;
                            const durationText = item?.duration || item?.session_duration || item?.time_required;
                            const downtimeText = item?.downtime || item?.recovery_time;
                            const brandsRaw = item?.brand;
                            const brandList = Array.isArray(brandsRaw)
                              ? brandsRaw
                              : (brandsRaw ? String(brandsRaw).split(',') : []);
                            const brands = brandList.map((b) => String(b || '').trim()).filter(Boolean);
                            const hasNetPrice = toNumberOrNull(item?.net_price) != null;
                            const hasAmountPrice = toNumberOrNull(item?.amount) != null;
                            const showTreatmentQuantity = hasAmountPrice;
                            const sessionsForPrice = showTreatmentQuantity ? Math.max(1, cartQuantity || 0) : 1;
                            const hasAnyPrice = hasNetPrice || hasAmountPrice;
                            const discountBadge = hasNetPrice ? formatDiscountBadge(item) : null;
                            const net = toNumberOrNull(item?.net_price);
                            const sale = toNumberOrNull(item?.sale_price);
                            const amount = toNumberOrNull(item?.amount);
                            return (
                              <div key={`${title}-${idx}`} className="skin-routine-treatment-card">

                                {/* ΓöÇΓöÇ Hero image with title overlay ΓöÇΓöÇ */}
                                <div className="skin-routine-treatment-hero">
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={title}
                                      className="skin-routine-treatment-image"
                                      onError={(e) => { e.currentTarget.parentElement.classList.add('no-image'); }}
                                    />
                                  ) : null}
                                  <div className="skin-routine-treatment-hero-overlay" />
                                  {discountBadge ? <div className="skin-routine-treatment-discount-badge">{discountBadge}</div> : null}
                                  {aboutText ? (
                                    <button
                                      type="button"
                                      className="skin-routine-treatment-about-icon"
                                      onClick={() => setAboutPopup({ open: true, title, text: aboutText })}
                                      aria-label="About this treatment"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                    </button>
                                  ) : null}
                                  <div className="skin-routine-treatment-title">{title}</div>
                                </div>

                                {/* ΓöÇΓöÇ Body ΓöÇΓöÇ */}
                                <div className="skin-routine-treatment-body">

                                  {recommendationLine ? (
                                    <div className="skin-routine-treatment-row">
                                      <span className="skin-routine-treatment-mini-heading">Why recommended</span>
                                      <div className="skin-routine-treatment-why-text">{recommendationLine}</div>
                                    </div>
                                  ) : null}



                                  {majorUspPlain ? (
                                    <div className="skin-routine-treatment-row">
                                      <span className="skin-routine-treatment-mini-heading">Key benefit</span>
                                      <div className="skin-routine-treatment-usp-text">{majorUspPlain}</div>
                                    </div>
                                  ) : null}

                                  {/* chips row: sessions / duration / downtime */}
                                  {(sessionsText || durationText || downtimeText) ? (
                                    <div className="skin-routine-treatment-meta-chips">
                                      {sessionsText ? <span className="skin-routine-treatment-chip">ΓÅ▒ {sessionsText} sessions</span> : null}
                                      {durationText ? <span className="skin-routine-treatment-chip">≡ƒòÉ {durationText}</span> : null}
                                      {downtimeText ? <span className="skin-routine-treatment-chip">≡ƒÆñ {downtimeText} downtime</span> : null}
                                    </div>
                                  ) : null}

                                  {/* condition tags */}
                                  {item?.conditions?.length > 0 ? (
                                    <div className="skin-routine-treatment-tags-area">
                                      <div className="skin-routine-treatment-tags-wrap">
                                        <span className="skin-routine-treatment-mini-heading">Addresses</span>
                                        <div className="skin-routine-treatment-tags">
                                          {item.conditions.map((c, i) => (
                                            <span key={`${c}-${i}`} className="skin-routine-treatment-tag skin-routine-treatment-tag--condition">{c}</span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}

                                  {brands.length > 0 ? (
                                    <div className="skin-routine-treatment-brand-row">
                                      <span className="skin-routine-treatment-brand-label">Brand</span>
                                      {brands.map((b, i) => (
                                        <span key={`${b}-${i}`} className="skin-routine-treatment-brand-chip">{b}</span>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>

                                {/* ΓöÇΓöÇ Footer: price + CTA ΓöÇΓöÇ */}
                                <div className="skin-routine-treatment-footer">
                                  <div className="skin-routine-treatment-price-block">
                                    {hasAnyPrice ? (
                                      hasNetPrice && hasDiscount(item) ? (
                                        <>
                                          <span className="skin-routine-treatment-price-slash">{formatPrice((net ?? 0) * sessionsForPrice)}</span>
                                          <span className="skin-routine-treatment-price">{formatPrice((sale ?? net ?? 0) * sessionsForPrice)}</span>
                                        </>
                                      ) : (
                                        <span className="skin-routine-treatment-price">{formatPrice((sale ?? net ?? amount ?? 0) * sessionsForPrice)}</span>
                                      )
                                    ) : <span className="skin-routine-treatment-price-free">Consultation</span>}
                                  </div>
                                  <div className="skin-routine-treatment-cta">
                                    {showTreatmentQuantity ? (
                                      <ProductCartCounter
                                        key={`routine-tc-${cartKey}`}
                                        product={item}
                                        onAddToCart={(t, quantity) => handleAddTreatmentToCart(t, quantity, cartKey)}
                                        disabled={false}
                                        initialQuantity={cartQuantity}
                                        quantityLabel="Sessions"
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        className={`skin-routine-treatment-cta-btn ${cartQuantity > 0 ? 'is-selected' : ''}`}
                                        onClick={() => handleAddTreatmentToCart(item, cartQuantity > 0 ? 0 : 1, cartKey)}
                                      >
                                        {cartQuantity > 0 ? 'Γ£ô Requested' : 'Request'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="skin-routine-summary-text">No treatment recommendations available yet.</p>
                      )}

                    </div>
                  </div>
                )}

                {suggestConfigLoaded && productSuggestionEnabled && hasCompletedTreatmentsStep && (
                  <div className="skin-routine-section-block skin-routine-products-block" id="section-products">
                    <h2 className="skin-routine-section-heading">
                      <span className="skin-routine-section-heading-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                      </span>
                      Recommended Products
                    </h2>
                    <div className="skin-routine-section-content">
                      <div className="skin-routine-products-toggle-wrap">
                        <div className="skin-routine-products-toggle-card">
                          <div className="skin-routine-products-toggle-copy">
                            <p className="skin-routine-products-toggle-eyebrow">Personalized Product Picks</p>
                            <p className="skin-routine-products-toggle-caption">
                              Reveal products matched to your concerns and goals.
                            </p>
                          </div>
                          <button
                            type="button"
                            className="skin-routine-products-toggle-btn"
                            onClick={() => setShowRecommendedProducts((prev) => !prev)}
                          >
                            {showRecommendedProducts ? 'Hide Recommended Products' : 'Show Recommended Products'}
                          </button>
                        </div>
                      </div>

                      {showRecommendedProducts && (isProductsLoading && recommendedProducts.morning.length === 0 && recommendedProducts.evening.length === 0 ? (
                        <div className="skin-routine-summary-loading">
                          <div className="loading-spinner"></div>
                          <p className="skin-routine-summary-loading-text">Generating recommended products...</p>
                        </div>
                      ) : (recommendedProducts.morning.length > 0 || recommendedProducts.evening.length > 0) ? (
                        <div className="skin-routine-treatments-grid">
                          {[...recommendedProducts.morning, ...recommendedProducts.evening].map((product, idx) => {
                            const title = product?.product_name || product?.name || `Product ${idx + 1}`;
                            const productId = product?.product_id || product?.id || `routine_product_${title}_${idx}`;
                            const cartQuantity = productCartItems[productId]?.quantity || 0;
                            const imageUrl = resolveProductImageUrl(product?.image_url);
                            const recommendationLine = getRecommendationLine(product)
                              .replace(/\s*ΓÇö\s*aligns with \d+ of your selected goal\(s\)/i, '')
                              .replace(/^aligns with \d+ selected goal\(s\)$/i, '')
                              .trim();
                            const aboutText = product?.description || product?.major_usp || '';
                            const majorUspPlain = product?.major_usp ? stripHtml(product.major_usp) : '';
                            const hasAnyPrice = toNumberOrNull(product?.amount) != null;
                            const discountBadge = hasAnyPrice ? formatDiscountBadge(product) : null;
                            const net = toNumberOrNull(product?.amount);
                            const sale = toNumberOrNull(product?.sale_price);
                            const qtyForPrice = Math.max(1, cartQuantity || 0);
                            const concerns = Array.isArray(product?.concerns_treated) ? product.concerns_treated : [];
                            return (
                              <div key={`${productId}-${idx}`} className="skin-routine-treatment-card">
                                <div className="skin-routine-treatment-hero">
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={title}
                                      className="skin-routine-treatment-image"
                                      onError={(e) => { e.currentTarget.parentElement.classList.add('no-image'); }}
                                    />
                                  ) : null}
                                  <div className="skin-routine-treatment-hero-overlay" />
                                  {discountBadge ? <div className="skin-routine-treatment-discount-badge">{discountBadge}</div> : null}
                                  {aboutText ? (
                                    <button
                                      type="button"
                                      className="skin-routine-treatment-about-icon"
                                      onClick={() => setAboutPopup({ open: true, title, text: aboutText })}
                                      aria-label="About this product"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                    </button>
                                  ) : null}
                                  <div className="skin-routine-treatment-title">{title}</div>
                                </div>

                                <div className="skin-routine-treatment-body">
                                  {recommendationLine ? (
                                    <div className="skin-routine-treatment-row">
                                      <span className="skin-routine-treatment-mini-heading">Why recommended</span>
                                      <div className="skin-routine-treatment-why-text">{recommendationLine}</div>
                                    </div>
                                  ) : null}

                                  {majorUspPlain ? (
                                    <div className="skin-routine-treatment-row">
                                      <span className="skin-routine-treatment-mini-heading">Key benefit</span>
                                      <div className="skin-routine-treatment-usp-text">{majorUspPlain}</div>
                                    </div>
                                  ) : null}

                                  {concerns.length > 0 ? (
                                    <div className="skin-routine-treatment-tags-area">
                                      <div className="skin-routine-treatment-tags-wrap">
                                        <span className="skin-routine-treatment-mini-heading">Treats</span>
                                        <div className="skin-routine-treatment-tags">
                                          {concerns.map((c, i) => (
                                            <span key={`${c}-${i}`} className="skin-routine-treatment-tag skin-routine-treatment-tag--condition">{c}</span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>

                                <div className="skin-routine-treatment-footer">
                                  <div className="skin-routine-treatment-price-block">
                                    {hasAnyPrice ? (
                                      (sale != null && sale > 0 && net != null && sale < net) ? (
                                        <>
                                          <span className="skin-routine-treatment-price-slash">{formatPrice((net ?? 0) * qtyForPrice)}</span>
                                          <span className="skin-routine-treatment-price">{formatPrice((sale ?? net ?? 0) * qtyForPrice)}</span>
                                        </>
                                      ) : (
                                        <span className="skin-routine-treatment-price">{formatPrice((sale ?? net ?? 0) * qtyForPrice)}</span>
                                      )
                                    ) : <span className="skin-routine-treatment-price-free">Consultation</span>}
                                  </div>
                                  <div className="skin-routine-treatment-cta">
                                    <ProductCartCounter
                                      key={`routine-pc-${productId}`}
                                      product={product}
                                      onAddToCart={(p, quantity) => handleAddProductToCart(p, quantity, productId)}
                                      disabled={false}
                                      initialQuantity={cartQuantity}
                                      quantityLabel="Qty"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="skin-routine-summary-error-wrap">
                          <p className="skin-routine-summary-text">No product recommendations available yet.</p>
                          <button
                            type="button"
                            className="skin-action-button skin-action-button-secondary"
                            onClick={fetchStoredProducts}
                              disabled={isProductsLoading}
                          >
                            {isProductsLoading ? 'LOADING...' : 'REFRESH PRODUCTS'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {getTotalCombinedCartItems() > 0 && (
                  <div className="skin-routine-next-treatments-inline">
                    <button
                      type="button"
                      className="skin-action-button skin-action-button-secondary skin-routine-home-inline-button"
                      onClick={handleOpenHomeConfirmPopup}
                    >
                      Home
                    </button>
                    <button
                      type="button"
                      className="skin-action-button skin-routine-continue-treatments-button"
                      onClick={handleOpenCheckoutModal}
                    >
                      CONFIRM SELECTED SUGGESTIONS ({getTotalCombinedCartItems()})
                    </button>
                  </div>
                )}

                {/* 4. AI Routine */}
                {showRoutine && (
                  <div className="skin-routine-section-block skin-routine-ai-routine-block" id="section-routine">
                    <h2 className="skin-routine-section-heading">
                      <span className="skin-routine-section-heading-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </span>
                      AI Routine
                    </h2>
                    <div className="skin-routine-section-content">
                      {isAiContentLoading && routine.morning.length === 0 && routine.evening.length === 0 ? (
                        <div className="skin-routine-routine-loading">
                          <div className="loading-spinner"></div>
                          <p className="skin-routine-routine-loading-text">
                            {recommendationGenerationState.inProgress
                              ? 'Generating recommended treatment and product...'
                              : 'Generating your personalized routine...'}
                          </p>
                        </div>
                      ) : error && routine.morning.length === 0 && routine.evening.length === 0 ? (
                        <div className="skin-routine-routine-error-wrap">
                          <p className="skin-routine-routine-error">{error}</p>
                          <button type="button" className="skin-action-button skin-action-button-secondary" onClick={() => window.location.reload()}>Retry</button>
                        </div>
                      ) : (routine.morning.length > 0 || routine.evening.length > 0) ? (
                        <>
                        {routine.morning.length > 0 && (
                          <div className="skin-routine-time-block">
                            <h3 className="skin-routine-time-title">ΓÿÇ∩╕Å Morning Routine</h3>
                            <ol className="skin-routine-steps-list">
                              {routine.morning.map((step, index) => (
                                <li key={index} className="skin-routine-step">{step}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {routine.evening.length > 0 && (
                          <div className="skin-routine-time-block">
                            <h3 className="skin-routine-time-title">≡ƒîÖ Evening Routine</h3>
                            <ol className="skin-routine-steps-list">
                              {routine.evening.map((step, index) => (
                                <li key={index} className="skin-routine-step">{step}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        </>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <PageFooter />
        </div>
      </main>

      <CombinedCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        productCartItemsMap={productCartItems}
        treatmentCartItemsMap={treatmentCartItems}
        onProductCartUpdate={handleProductCartUpdate}
        onTreatmentCartUpdate={handleTreatmentCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
      />

      {/* About Treatment Popup */}
      {aboutPopup.open && (
        <div className="skin-routine-about-popup-backdrop" onClick={() => setAboutPopup({ open: false, title: '', text: '' })}>
          <div className="skin-routine-about-popup" onClick={(e) => e.stopPropagation()}>
            <div className="skin-routine-about-popup-header">
              <h3 className="skin-routine-about-popup-title">{aboutPopup.title}</h3>
              <button
                type="button"
                className="skin-routine-about-popup-close"
                onClick={() => setAboutPopup({ open: false, title: '', text: '' })}
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="skin-routine-about-popup-body">
              {(() => {
                const raw = aboutPopup.text == null ? '' : String(aboutPopup.text);
                const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
                return <div dangerouslySetInnerHTML={{ __html: html }} />;
              })()}
            </div>
          </div>
        </div>
      )}

      {showHomeConfirmPopup && (
        <div className="skin-routine-home-confirm-backdrop" onClick={handleCloseHomeConfirmPopup}>
          <div className="skin-routine-home-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="skin-routine-home-confirm-header">
              <h3 className="skin-routine-home-confirm-title">Go To Home?</h3>
            </div>
            <p className="skin-routine-home-confirm-text">
              Are you sure you want to go to Home? Any unsaved selection updates on this page may be lost.
            </p>
            <div className="skin-routine-home-confirm-actions">
              <button
                type="button"
                className="skin-action-button skin-action-button-secondary skin-routine-home-confirm-cancel"
                onClick={handleCloseHomeConfirmPopup}
              >
                Stay Here
              </button>
              <button
                type="button"
                className="skin-action-button skin-routine-home-confirm-continue"
                onClick={handleConfirmGoHome}
              >
                Yes, Go Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* USP Popup */}
      {uspPopup.open && (() => {
        const raw = String(uspPopup.text);
        const isHtml = /<[a-z][\s\S]*>/i.test(raw);
        const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
        return (
          <div className="product-usp-popup-overlay" onClick={handleCloseUspPopup}>
            <div
              className="product-usp-popup"
              style={{ top: `${uspPopup.position.top}px`, left: `${uspPopup.position.left}px`, width: `${Math.max(uspPopup.position.width || 300, 300)}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="product-usp-popup-content">
                <button className="product-usp-popup-close" onClick={handleCloseUspPopup} aria-label="Close">├ù</button>
                {uspPopup.heading && <h3 className="product-usp-popup-heading">{uspPopup.heading}</h3>}
                <div className="product-usp-popup-text" dangerouslySetInnerHTML={{ __html: html }} />
              </div>
            </div>
          </div>
        );
      })()}

      {radarConcernModal.open && activeRadarConcernDetail && (
        <div className="skin-routine-radar-modal-backdrop" onClick={closeRadarConcernModal}>
          <div
            className="skin-routine-radar-modal"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              radarModalTouchStartXRef.current = e.touches?.[0]?.clientX ?? null;
            }}
            onTouchEnd={(e) => {
              const startX = radarModalTouchStartXRef.current;
              const endX = e.changedTouches?.[0]?.clientX ?? null;
              radarModalTouchStartXRef.current = null;
              if (startX == null || endX == null) return;
              const diff = endX - startX;
              if (Math.abs(diff) < 40) return;
              if (diff < 0) stepRadarConcernModal(1);
              else stepRadarConcernModal(-1);
            }}
          >
            <div className="skin-routine-radar-modal-header">
              <h3 className="skin-routine-radar-modal-title">{activeRadarConcernDetail.label}</h3>
              <button type="button" className="skin-routine-radar-modal-close" onClick={closeRadarConcernModal} aria-label="Close concern details">×</button>
            </div>
            <p className="skin-routine-radar-modal-subtitle">
              {activeRadarConcernDetail.beforeScore != null && activeRadarConcernDetail.afterScore != null
                ? `Before ${activeRadarConcernDetail.beforeScore}% -> After ${activeRadarConcernDetail.afterScore}%`
                : activeRadarConcernDetail.afterScore != null
                  ? `After ${activeRadarConcernDetail.afterScore}%`
                  : `Before ${activeRadarConcernDetail.beforeScore != null ? `${activeRadarConcernDetail.beforeScore}%` : '--'}`}
            </p>
            <p className="skin-routine-radar-modal-subtitle skin-routine-radar-modal-dates">
              From {previousAnalysisDateText || '--'} to {analysisDateText || '--'}
            </p>
            <svg className="skin-routine-radar-modal-linechart" viewBox="0 0 320 160" role="img" aria-label={`${activeRadarConcernDetail.label} trend`}>
              <line x1="42" y1="20" x2="42" y2="132" stroke="rgba(100, 116, 139, 0.35)" strokeWidth="1.2" />
              <line x1="42" y1="132" x2="286" y2="132" stroke="rgba(100, 116, 139, 0.35)" strokeWidth="1.2" />
              {[0, 25, 50, 75, 100].map((tick) => {
                const y = 132 - (tick / 100) * 108;
                return <line key={`modal-tick-${tick}`} x1="42" y1={y} x2="286" y2={y} stroke="rgba(148, 163, 184, 0.16)" strokeWidth="1" />;
              })}
              {activeRadarConcernDetail.beforeScore != null && activeRadarConcernDetail.afterScore != null ? (
                <>
                  <line
                    x1="90"
                    y1={132 - (activeRadarConcernDetail.beforeScore / 100) * 108}
                    x2="240"
                    y2={132 - (activeRadarConcernDetail.afterScore / 100) * 108}
                    stroke={activeRadarConcernDetail.delta != null && activeRadarConcernDetail.delta >= 0 ? 'rgba(22,163,74,0.95)' : 'rgba(220,38,38,0.95)'}
                    strokeWidth="3"
                  />
                  <circle cx="90" cy={132 - (activeRadarConcernDetail.beforeScore / 100) * 108} r="5" fill="rgba(71, 85, 105, 0.95)" />
                  <circle cx="240" cy={132 - (activeRadarConcernDetail.afterScore / 100) * 108} r="5" fill="rgba(15, 118, 110, 0.95)" />
                </>
              ) : activeRadarConcernDetail.afterScore != null ? (
                <circle cx="240" cy={132 - (activeRadarConcernDetail.afterScore / 100) * 108} r="5" fill="rgba(15, 118, 110, 0.95)" />
              ) : (
                <circle cx="90" cy={132 - ((activeRadarConcernDetail.beforeScore || 0) / 100) * 108} r="5" fill="rgba(71, 85, 105, 0.95)" />
              )}
              <text x="90" y="148" textAnchor="middle" className="skin-routine-radar-modal-axis">{previousAnalysisDateText || 'Before'}</text>
              <text x="240" y="148" textAnchor="middle" className="skin-routine-radar-modal-axis">{analysisDateText || 'After'}</text>
            </svg>
            <div className="skin-routine-radar-modal-legend">
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before</span>
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After</span>
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved line</span>
              <span className="skin-routine-radar-modal-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Decline line</span>
            </div>
            <div className="skin-routine-radar-modal-actions">
              <button type="button" className="skin-routine-radar-modal-nav" onClick={() => stepRadarConcernModal(-1)}>Prev</button>
              <button type="button" className="skin-routine-radar-modal-nav" onClick={() => stepRadarConcernModal(1)}>Next</button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Radar Chart Modal */}
      {isRadarEnlargedOpen && (
        <div className="skin-routine-radar-enlarged-backdrop" onClick={() => setIsRadarEnlargedOpen(false)}>
          <div
            className="skin-routine-radar-enlarged-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="skin-routine-radar-enlarged-close"
              onClick={() => setIsRadarEnlargedOpen(false)}
              aria-label="Close enlarged radar view"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="skin-routine-radar-enlarged-content">
              {/* Tabs for Before/After */}
              {hasBeforeAfterRadar && (
                <div className="skin-routine-radar-enlarged-tabs" role="tablist" aria-label="Enlarged skin analysis report comparison tabs">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={radarComparisonTab === 'before'}
                    className={`skin-routine-radar-enlarged-tab ${radarComparisonTab === 'before' ? 'active' : ''}`}
                    onClick={() => setRadarComparisonTab('before')}
                  >
                    Before
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={radarComparisonTab === 'after'}
                    className={`skin-routine-radar-enlarged-tab ${radarComparisonTab === 'after' ? 'active' : ''}`}
                    onClick={() => setRadarComparisonTab('after')}
                  >
                    After
                  </button>
                </div>
              )}
              
              <h3 className="skin-routine-radar-enlarged-title">
                {hasBeforeAfterRadar ? (radarComparisonTab === 'before' ? 'Before Skin Analysis Report' : 'After Skin Analysis Report') : 'Skin Analysis Report'}
              </h3>
              <div className="skin-routine-radar-enlarged-info">
                {isAfterComparisonView ? (
                  <>
                    <span>Score: {overallSkinScore != null ? `${overallSkinScore}/100` : '--'}</span>
                    <span>{previousAnalysisDateText || '--'} → {analysisDateText || '--'}</span>
                  </>
                ) : (
                  <>
                    <span>Score: {activeRadarScore != null ? `${activeRadarScore}/100` : '--'}</span>
                    <span>{activeRadarDate || '--'}</span>
                  </>
                )}
              </div>
              <div className="skin-routine-radar-enlarged-chart-wrapper">
                <svg className="skin-routine-radar-enlarged-svg" viewBox={`0 0 ${activeRadarModel.size} ${activeRadarModel.size}`} role="img" aria-label="Enlarged skin analysis report chart">
                  {/* Grid Circles */}
                  {activeRadarModel.levels.map((level) => {
                    const r = (level / 100) * activeRadarModel.radius;
                    return (
                      <circle
                        key={`level-${level}`}
                        cx={activeRadarModel.cx}
                        cy={activeRadarModel.cy}
                        r={r}
                        fill="none"
                        stroke="rgba(100, 116, 139, 0.22)"
                        strokeWidth="1"
                      />
                    );
                  })}
                  {/* Axis Lines */}
                  {activeRadarModel.axis.map((item, idx) => (
                    <line
                      key={`axis-${idx}`}
                      x1={activeRadarModel.cx}
                      y1={activeRadarModel.cy}
                      x2={activeRadarModel.cx + Math.cos(item.angle) * activeRadarModel.radius}
                      y2={activeRadarModel.cy + Math.sin(item.angle) * activeRadarModel.radius}
                      stroke="rgba(100, 116, 139, 0.2)"
                      strokeWidth="1"
                    />
                  ))}
                  {/* Polygons */}
                  {isAfterComparisonView ? (
                    <>
                      <polygon
                        points={activeRadarModel.beforePolygon}
                        fill="none"
                        stroke="rgba(249, 115, 22, 0.95)"
                        strokeWidth="2.2"
                        strokeDasharray="5 4"
                      />
                      <polygon
                        points={activeRadarModel.afterPolygon}
                        fill="none"
                        stroke="rgba(37, 99, 235, 0.95)"
                        strokeWidth="2.6"
                      />
                    </>
                  ) : (
                    <polygon
                      points={activeRadarModel.polygon}
                      fill="none"
                      stroke="rgba(37, 99, 235, 0.95)"
                      strokeWidth="2.6"
                    />
                  )}
                  {/* Data Points and Labels */}
                  {isAfterComparisonView ? (
                    <>
                      {activeRadarModel.axis.map((item, idx) => (
                        <g key={`compare-point-${idx}`}>
                          {/* Connection line between before/after */}
                          {item.hasBefore && item.hasAfter ? (
                            <line
                              x1={item.beforePointX}
                              y1={item.beforePointY}
                              x2={item.afterPointX}
                              y2={item.afterPointY}
                              stroke={
                                item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.95)'
                                  : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.95)'
                                    : 'rgba(148, 163, 184, 0.9)'
                              }
                              strokeWidth="2.4"
                              strokeDasharray={item.changeKind === 'stable' ? '3 3' : 'none'}
                            />
                          ) : null}
                          {/* Before point */}
                          {item.hasBefore ? <circle cx={item.beforePointX} cy={item.beforePointY} r="3.4" fill="rgba(249, 115, 22, 0.95)" /> : null}
                          {/* After point */}
                          {item.hasAfter ? (
                            <circle
                              cx={item.afterPointX}
                              cy={item.afterPointY}
                              r="4.2"
                              fill={
                                item.changeKind === 'new' ? 'rgba(168, 85, 247, 0.98)'
                                  : item.changeKind === 'improved' ? 'rgba(22, 163, 74, 0.98)'
                                    : item.changeKind === 'declined' ? 'rgba(220, 38, 38, 0.98)'
                                      : 'rgba(37, 99, 235, 0.95)'
                              }
                            />
                          ) : null}
                          {/* Label */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY}
                            textAnchor={item.labelAnchor || 'middle'}
                            className={`skin-routine-radar-label skin-routine-radar-label--${item.changeKind}`}
                          >
                            {item.label}
                          </text>
                          {/* Score */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY + 15}
                            textAnchor={item.labelAnchor || 'middle'}
                            className={`skin-routine-radar-score skin-routine-radar-score--${item.changeKind}`}
                          >
                            {item.changeKind === 'new'
                              ? `A:${item.afterScore != null ? `${item.afterScore}%` : '--'} New`
                              : item.changeKind === 'missing'
                                ? `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} Missing`
                                : `B:${item.beforeScore != null ? `${item.beforeScore}%` : '--'} A:${item.afterScore != null ? `${item.afterScore}%` : '--'} ${item.delta != null ? `${item.delta > 0 ? '+' : ''}${item.delta}` : ''}`}
                          </text>
                        </g>
                      ))}
                    </>
                  ) : (
                    <>
                      {activeRadarModel.axis.map((item, idx) => (
                        <g key={`point-${idx}`}>
                          {/* Point */}
                          <circle cx={item.pointX} cy={item.pointY} r="4" fill="rgba(37, 99, 235, 0.95)" />
                          {/* Label */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY}
                            textAnchor={item.labelAnchor || 'middle'}
                            className="skin-routine-radar-label"
                          >
                            {item.label}
                          </text>
                          {/* Score */}
                          <text
                            x={item.labelX + (item.labelOffsetX || 0)}
                            y={item.labelY + 15}
                            textAnchor={item.labelAnchor || 'middle'}
                            className="skin-routine-radar-score"
                          >
                            {item.score}%
                          </text>
                        </g>
                      ))}
                    </>
                  )}
                </svg>
              </div>
              {/* Legend */}
              <div className="skin-routine-radar-legend" aria-label="Radar chart color legend">
                {isAfterComparisonView ? (
                  <>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--before" />Before line</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />After line</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--improved" />Improved</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--declined" />Needs attention</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--new" />New in after</span>
                    <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--stable" />Stable</span>
                  </>
                ) : (
                  <span className="skin-routine-radar-legend-item"><span className="skin-routine-radar-legend-swatch skin-routine-radar-legend-swatch--after" />Current scan line</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBackToTop && (
        <button
          type="button"
          className="skin-routine-back-to-top"
          onClick={handleScrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default SkinRoutinePage;